import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Send, CheckCircle, Upload, X, Mail, Shield, ExternalLink, Clock, Compass, FileText, Briefcase, Sparkles, Languages, LockKeyhole, GraduationCap, ClipboardCheck } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../lib/supabase';
import { getSafeErrorMessage, toSafeDisplayString } from '../lib/apiErrors';
import { confirmMasterProfile } from '../data/candidateApi';
import MasterCvReview from '../components/MasterCvReview';
import { OnboardingCard, OnboardingMilestones } from '../components/onboarding/OnboardingPrimitives';
import { authCallbackUrl, createPendingIntent } from '../lib/pendingIntent';
import { resolveReturningCandidateRoute } from '../lib/trackFContracts';
import { buildExactJobApplicationRoute, buildExactJobRequest, buildRefinementRequest, shouldRunExactJobAnalysis } from '../lib/trackDExactJobContracts';
import { CHECK_FIT_JOB_KEY, readSelectedStoredJobId, clearSelectedStoredJobState, isCareerProfileResumeRoute } from '../lib/trackAExactJobIntent';
import {
  CANDIDATE_INTAKE_PREFILL_KEY,
  LEGACY_PHASE_1B_PREFILL_KEY,
  availabilityOptions,
  createCandidateIntakeDraft,
  joinList,
  noticePeriodOptions,
  parseCandidateIntakePrefill,
  reasonForLookingOptions,
  splitList,
  thingsToAvoidOptions,
  uniqueArray,
  waysOfWorkingOptions,
} from '../data/candidateIntake';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const safeFindJobsText = (value) => toSafeDisplayString(value, { context: 'find-jobs-submit' });
const FIND_JOBS_DRAFT_VERSION = 'find-jobs-browser-draft-v1';
const FIND_JOBS_AUTOSAVE_DELAY_MS = 700;
const TOTAL_FIND_JOBS_STEPS = 10;
const DIRECT_LEVEL_OPTIONS = ['Entry-level', 'Junior', 'Associate', 'Mid-level', 'Senior', 'Lead', 'Manager', 'Not sure yet'];
const EMPLOYMENT_TYPE_OPTIONS = ['Internship', 'Working student', 'Apprenticeship', 'Graduate scheme', 'Permanent'];
const LANGUAGE_PROFICIENCY_OPTIONS = ['Native', 'Fluent', 'Professional working', 'Conversational', 'Basic', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const GERMAN_LEVEL_OPTIONS = [
  { value: 'None', label: 'None' },
  { value: 'A1', label: 'A1 - Beginner' },
  { value: 'A2', label: 'A2 - Elementary' },
  { value: 'B1', label: 'B1 - Intermediate' },
  { value: 'B2', label: 'B2 - Upper Intermediate' },
  { value: 'C1', label: 'C1 - Advanced' },
  { value: 'C2', label: 'C2 - Proficient' },
  { value: 'Native', label: 'Native' },
];
const EXPERIENCE_BAND_OPTIONS = [
  { value: '0-1', label: '0-1 years' },
  { value: '1-3', label: '1-3 years' },
  { value: '3-5', label: '3-5 years' },
  { value: '5-7', label: '5-7 years' },
  { value: '7-10', label: '7-10 years' },
  { value: '10+', label: '10+ years' },
];
const WORK_AUTHORISATION_OPTIONS = [
  { value: 'EU Citizen', label: 'EU citizen / unrestricted EU work rights' },
  { value: 'UK Work Authorization', label: 'UK work authorisation' },
  { value: 'Current Work Permit', label: 'Current work permit' },
  { value: 'Student Visa', label: 'Student visa' },
  { value: 'Graduate Route', label: 'Graduate / job seeker route' },
  { value: 'Need Sponsorship', label: 'Need sponsorship' },
  { value: 'Not Sure', label: 'Not sure yet' },
];
const EDUCATION_STATUS_OPTIONS = ['Clear in my CV', 'I want to add it manually', 'Not relevant for this search', 'Not sure yet'];
const SALARY_PERIOD_OPTIONS = ['Per year', 'Per month', 'Per hour'];
const SALARY_MARKET_CONFIG = {
  Germany: { label: 'Germany', currency: 'EUR' },
  'London / UK': { label: 'London / UK', currency: 'GBP' },
  'Remote Europe': { label: 'Remote Europe', currency: '' },
};

const findJobsSteps = [
  { id: 1, title: 'Confirm your direction', icon: Compass },
  { id: 2, title: 'Add your CV', icon: FileText },
  { id: 3, title: 'Add current context', icon: Briefcase },
  { id: 4, title: 'Add skills and keywords', icon: Sparkles },
  { id: 5, title: 'How do you work best?', icon: Sparkles },
  { id: 6, title: 'Add language details', icon: Languages },
  { id: 7, title: 'Add work authorisation context', icon: LockKeyhole },
  { id: 8, title: 'Private things to avoid', icon: Shield },
  { id: 9, title: 'Optional salary and education', icon: GraduationCap },
  { id: 10, title: 'Consent and continue', icon: ClipboardCheck },
];

function findJobsDraftKey(userId) {
  return userId ? `jobmatchpro:find-jobs-draft:${userId}` : '';
}

function readFindJobsDraft(userId) {
  if (typeof window === 'undefined' || !userId) return null;
  try {
    const raw = window.localStorage.getItem(findJobsDraftKey(userId));
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || parsed.schema_version !== FIND_JOBS_DRAFT_VERSION || parsed.user_id !== userId) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeFindJobsDraft(userId, formData, consentGiven, currentStep, draftSubmissionId, selectedStoredJobId) {
  if (typeof window === 'undefined' || !userId) return false;
  try {
    window.localStorage.setItem(findJobsDraftKey(userId), JSON.stringify({
      schema_version: FIND_JOBS_DRAFT_VERSION,
      user_id: userId,
      saved_at: new Date().toISOString(),
      form_data: formData,
      consent_given: Boolean(consentGiven),
      current_step: currentStep,
      draft_submission_id: draftSubmissionId || null,
      selected_stored_job_id: selectedStoredJobId || null,
    }));
    return true;
  } catch (_) {
    return false;
  }
}

function clearFindJobsDraft(userId) {
  if (typeof window === 'undefined' || !userId) return;
  try {
    window.localStorage.removeItem(findJobsDraftKey(userId));
  } catch (_) {
    return;
  }
}

function getApiErrorCode(value) {
  if (!value || typeof value !== 'object') return '';
  if (typeof value.code === 'string') return value.code;
  if (value.detail && typeof value.detail === 'object' && typeof value.detail.code === 'string') return value.detail.code;
  if (value.message && typeof value.message === 'object' && typeof value.message.code === 'string') return value.message.code;
  if (value.error && typeof value.error === 'object' && typeof value.error.code === 'string') return value.error.code;
  return '';
}

function hasCareerAgentPrefill(value) {
  return Boolean(
    value
    && (
      value.target_roles
      || (Array.isArray(value.target_roles_list) && value.target_roles_list.length)
      || value.target_locations
      || (Array.isArray(value.target_locations_list) && value.target_locations_list.length)
      || value.target_seniority
      || (Array.isArray(value.target_seniority_levels) && value.target_seniority_levels.length)
    ),
  );
}

function FromCareerAgentHint({ show }) {
  if (!show) return null;
  return (
    <p className="text-xs text-[#6f6657]" data-testid="career-agent-prefill-helper">
      Brought over from your saved direction. You can edit this before search.
    </p>
  );
}

function splitSummaryValues(value) {
  return splitList(value);
}

function compactSummaryValue(value, maxVisible = 3) {
  const items = splitSummaryValues(value);
  if (!items.length) return '';
  if (items.length <= maxVisible) return items.join(', ');
  return `${items.slice(0, maxVisible).join(', ')} +${items.length - maxVisible} more`;
}

function skillPanelValue(value) {
  const skills = splitList(value);
  if (!skills.length) return '';
  return skills.length <= 3 ? skills.join(', ') : `${skills.length} skills added`;
}

function normalizeLanguageRows(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => ({
        language: String(item?.language || '').trim(),
        proficiency: String(item?.proficiency || '').trim(),
      }))
      .filter((item) => item.language || item.proficiency);
  }
  return splitList(value)
    .map((item) => {
      const match = String(item).trim().match(/^(.+?)\s*(?:\((.+)\)|[-:]\s*(.+))$/);
      if (match) return { language: match[1].trim(), proficiency: (match[2] || match[3] || '').trim() };
      const parts = String(item).trim().split(/\s+/);
      const maybeLevel = parts.length > 1 ? parts[parts.length - 1] : '';
      if (LANGUAGE_PROFICIENCY_OPTIONS.some((level) => level.toLowerCase() === maybeLevel.toLowerCase())) {
        return { language: parts.slice(0, -1).join(' '), proficiency: maybeLevel };
      }
      return { language: String(item).trim(), proficiency: '' };
    })
    .filter((item) => item.language || item.proficiency);
}

function formatLanguageRows(rows) {
  return normalizeLanguageRows(rows)
    .filter((row) => row.language)
    .map((row) => row.proficiency ? `${row.language} (${row.proficiency})` : row.language)
    .join(', ');
}

function DirectionChipGroup({ label, values, emptyText, testid }) {
  const items = splitSummaryValues(values);
  return (
    <div className="space-y-2" data-testid={testid}>
      <p className="text-xs font-medium uppercase tracking-[0.16em] text-[#7b694d]">{label}</p>
      <div className="flex flex-wrap gap-2">
        {items.length ? items.map((item) => (
          <span key={`${label}-${item}`} className="rounded-full border border-[#d7c5a7] bg-white/60 px-3 py-1.5 text-sm text-[#30473e]">
            {item}
          </span>
        )) : (
          <span className="rounded-full border border-[#d7c5a7] bg-[#fff4dc] px-3 py-1.5 text-sm text-[#6f5b2f]">
            {emptyText}
          </span>
        )}
      </div>
    </div>
  );
}

function formatSalaryFloor(salaryFloor) {
  const amount = String(salaryFloor?.amount || '').trim();
  if (!amount) return '';
  return [amount, salaryFloor?.currency, salaryFloor?.period].filter(Boolean).join(' ');
}

function activeSalaryMarkets(formData) {
  const markets = splitList(formData.target_locations);
  if (markets.includes('Not sure yet')) return [];
  return markets.filter((market) => SALARY_MARKET_CONFIG[market]);
}

function salaryFloorForMarket(market, current = {}) {
  const config = SALARY_MARKET_CONFIG[market] || { currency: '' };
  const fixedCurrency = market === 'Germany' || market === 'London / UK';
  return {
    amount: String(current.amount || ''),
    currency: fixedCurrency ? String(config.currency || '') : String(current.currency || config.currency || ''),
    period: String(current.period || 'Per year'),
  };
}

function normalizeSalaryFloorsByMarket(value, markets = []) {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const next = { ...source };
  markets.forEach((market) => {
    next[market] = salaryFloorForMarket(market, source[market]);
  });
  return next;
}

function formatSalaryFloorsByMarket(floors, markets = []) {
  return markets
    .map((market) => {
      const label = SALARY_MARKET_CONFIG[market]?.label || market;
      const text = formatSalaryFloor((floors || {})[market]);
      return text ? `${label}: ${text}` : '';
    })
    .filter(Boolean)
    .join('; ');
}

function salaryFloorErrors(formData) {
  const newErrors = {};
  const markets = activeSalaryMarkets(formData);
  const floors = normalizeSalaryFloorsByMarket(formData.salaryFloorsByMarket, markets);
  markets.forEach((market) => {
    const floor = floors[market] || {};
    const amount = String(floor.amount || '').trim();
    const key = market.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    if (!amount) return;
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) newErrors[`salary_${key}_amount`] = `Enter a positive salary amount for ${market}`;
    if (!floor.currency) newErrors[`salary_${key}_currency`] = `Select a currency for ${market}`;
    if (!floor.period) newErrors[`salary_${key}_period`] = `Select a period for ${market}`;
  });
  return newErrors;
}

function hasGermanRelevantMarket(formData) {
  return splitList(formData.target_locations).some((market) => ['Germany', 'Remote Europe'].includes(market));
}

function signalCount(formData, cvFile, candidateIntakeDraft) {
  return [
    splitList(formData.target_roles).length,
    splitList(formData.target_locations).length,
    formData.target_seniority || formData.target_seniority_levels?.length,
    candidateIntakeDraft?.careerAgent?.employmentTypes?.length,
    candidateIntakeDraft?.careerAgent?.workSetup?.length,
    candidateIntakeDraft?.careerAgent?.openToRelocateEurope,
    cvFile,
    formData.job_title,
    formData.location,
    formData.years_experience_band,
    splitList(formData.core_skills).length,
    normalizeLanguageRows(formData.languageRows || formData.languages).length,
    formData.german_level && hasGermanRelevantMarket(formData),
    formData.visa_status,
    formData.reason_for_looking,
    formData.availability,
    formData.notice_period,
    formData.waysOfWorking?.length,
    formData.thingsToAvoid?.length,
    formatSalaryFloorsByMarket(formData.salaryFloorsByMarket, activeSalaryMarkets(formData)),
    formData.education,
  ].filter(Boolean).length;
}

function AgentStatusLine({ label, value, status = 'Optional', testid }) {
  return (
    <div className="rounded-2xl border border-[#d7eadc]/15 bg-[#244f45]/70 p-3" data-testid={testid}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="text-[11px] uppercase tracking-[0.14em] text-[#c6d8cb]">{label}</p>
        <span className="rounded-full bg-[#fff8ed]/10 px-2 py-0.5 text-[10px] text-[#dce9de]">{status}</span>
      </div>
      <p className="text-sm leading-relaxed text-[#fff8ed]">{value || 'Skipped for now'}</p>
    </div>
  );
}

function CareerAgentFindJobsPanel({ formData, cvFile, candidateIntakeDraft, currentStep }) {
  const agent = candidateIntakeDraft?.careerAgent || {};
  const salary = formatSalaryFloorsByMarket(formData.salaryFloorsByMarket, activeSalaryMarkets(formData));
  const languagesValue = formatLanguageRows(formData.languageRows || formData.languages);
  const nextMap = {
    0: 'Sign in to continue',
    1: 'Confirm your search direction',
    2: 'Add your CV',
    3: 'Add current context',
    4: 'Add search skills',
    5: 'Add language details',
    6: 'Add work authorisation context',
    7: 'Add optional filters',
    8: 'Review consent and create draft',
  };
  return (
    <aside className="hidden max-h-[calc(100vh-4rem)] overflow-y-auto rounded-[2rem] bg-[#173d35] p-6 text-[#fff8ed] shadow-2xl shadow-[#6a4d2e]/10 lg:sticky lg:top-8 lg:block lg:self-start" data-testid="find-jobs-career-agent-panel">
                    <p className="mb-2 text-xs uppercase tracking-[0.22em] text-[#c6d8cb]">Your saved direction</p>
      <h2 className="mb-5 font-serif-display text-2xl font-medium">Search profile</h2>
      <div className="space-y-3">
        <AgentStatusLine label="Direction" value={compactSummaryValue(formData.target_roles)} status={formData.target_roles ? 'Confirmed' : 'Needs review'} testid="agent-panel-direction" />
        <AgentStatusLine label="Markets" value={compactSummaryValue(formData.target_locations)} status={formData.target_locations ? 'Confirmed' : 'Needs review'} testid="agent-panel-markets" />
        <AgentStatusLine label="Level" value={compactSummaryValue(formData.target_seniority_levels?.length ? formData.target_seniority_levels : formData.target_seniority)} status={formData.target_seniority || formData.target_seniority_levels?.length ? 'Added' : 'Optional'} testid="agent-panel-level" />
        <AgentStatusLine label="Employment" value={compactSummaryValue(agent.employmentTypes)} status={agent.employmentTypes?.length ? 'Added' : 'Optional'} testid="agent-panel-employment" />
        <AgentStatusLine label="Work setup" value={compactSummaryValue(agent.workSetup)} status={agent.workSetup?.length ? 'Added' : 'Optional'} testid="agent-panel-work-setup" />
        <AgentStatusLine label="Relocation" value={agent.openToRelocateEurope ? 'Open to relocate in Europe' : ''} status={agent.openToRelocateEurope ? 'Added' : 'Optional'} testid="agent-panel-relocation" />
        <AgentStatusLine label="Evidence" value={cvFile ? 'CV added, evidence review pending' : 'CV not added'} status={cvFile ? 'Needs review' : 'Needs review'} testid="agent-panel-evidence" />
        <AgentStatusLine label="Current context" value={compactSummaryValue([formData.job_title, formData.location, formData.years_experience_band, formData.reason_for_looking, formData.availability].filter(Boolean))} status={formData.job_title || formData.location || formData.years_experience_band || formData.reason_for_looking || formData.availability ? 'Added' : 'Optional'} testid="agent-panel-context" />
        <AgentStatusLine label="Search signals" value={skillPanelValue(formData.core_skills)} status={formData.core_skills ? 'Added' : 'Optional'} testid="agent-panel-skills" />
        <AgentStatusLine label="Ways of working" value={compactSummaryValue(formData.waysOfWorking || [])} status={formData.waysOfWorking?.length ? 'Added' : 'Optional'} testid="agent-panel-ways-of-working" />
        <AgentStatusLine label="Languages" value={compactSummaryValue([languagesValue, hasGermanRelevantMarket(formData) && formData.german_level ? `German ${formData.german_level}` : ''].filter(Boolean))} status={languagesValue || formData.german_level ? 'Added' : 'Optional'} testid="agent-panel-languages" />
        <AgentStatusLine label="Eligibility" value={formData.visa_status} status={formData.visa_status ? 'Added' : 'Optional'} testid="agent-panel-eligibility" />
        <AgentStatusLine label="Private avoid list" value={formData.thingsToAvoid?.length ? `${formData.thingsToAvoid.length} private item${formData.thingsToAvoid.length === 1 ? '' : 's'}` : ''} status={formData.thingsToAvoid?.length ? 'Added' : 'Optional'} testid="agent-panel-avoid-list" />
        <AgentStatusLine label="Optional filters" value={[salary, formData.education].filter(Boolean).join(', ')} status={salary || formData.education ? 'Added' : 'Optional'} testid="agent-panel-filters" />
        <AgentStatusLine label="Next" value={nextMap[currentStep] || 'Continue'} status="Next" testid="agent-panel-next" />
      </div>
    </aside>
  );
}

function MobileCareerAgentSummary({ formData, cvFile, candidateIntakeDraft, currentStep }) {
  const [open, setOpen] = useState(false);
  const toggleRef = useRef(null);
  const count = signalCount(formData, cvFile, candidateIntakeDraft);
  const closeSummary = () => {
    setOpen(false);
    window.setTimeout(() => toggleRef.current?.focus(), 0);
  };
  const nextText = currentStep === 0 ? 'Sign in to continue' : `Step ${currentStep} of ${TOTAL_FIND_JOBS_STEPS}`;
  return (
    <div className="sticky top-3 z-30 mb-4 lg:hidden" data-testid="mobile-career-agent-summary">
      <button
        ref={toggleRef}
        type="button"
        onClick={() => (open ? closeSummary() : setOpen(true))}
        aria-expanded={open}
        aria-controls="mobile-career-agent-summary-panel"
          aria-label={`${open ? 'Hide' : 'Show'} your saved direction summary. ${count} signals added.`}
        className="flex min-h-[44px] w-full items-center justify-between rounded-2xl border border-[#8b7652] bg-[#173d35] px-4 py-3 text-left text-sm font-medium text-[#fff8ed] shadow-lg"
        data-testid="mobile-career-agent-summary-toggle"
      >
          <span>Saved direction · {count} signals added</span>
        <span aria-hidden="true">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div id="mobile-career-agent-summary-panel" className="mt-2 max-h-[70vh] overflow-y-auto rounded-2xl border border-[#8b7652] bg-[#fff9ee] p-4 shadow-xl" data-testid="mobile-career-agent-summary-expanded">
          <DirectionChipGroup label="Direction" values={formData.target_roles} emptyText="Add roles" testid="mobile-summary-direction" />
          <DirectionChipGroup label="Evidence" values={cvFile ? ['CV added'] : []} emptyText="CV not added" testid="mobile-summary-evidence" />
          <DirectionChipGroup label="Search signals" values={formData.core_skills} emptyText="Optional" testid="mobile-summary-skills" />
          <DirectionChipGroup label="Eligibility" values={formData.visa_status ? [formData.visa_status] : []} emptyText="Optional" testid="mobile-summary-eligibility" />
          <p className="mt-3 text-sm text-[#6f6657]">Next: {nextText}</p>
        </div>
      )}
    </div>
  );
}

function CareerAgentDirectionStrip({ formData, candidateIntakeDraft, onEdit }) {
  const agent = candidateIntakeDraft?.careerAgent || {};
  return (
    <section className="mb-5 rounded-2xl border border-[#d8c8ab] bg-[#fff9ee]/80 p-4" data-testid="career-agent-direction-strip">
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-[#7b694d]">Saved direction</p>
          <p className="text-sm text-[#6f6657]">Already captured. Edit only if something changed.</p>
        </div>
        <button type="button" onClick={onEdit} className="min-h-[44px] rounded-md border border-[#8b7652] bg-white/60 px-3 py-2 text-sm font-medium text-[#173d35]" data-testid="edit-direction-strip-btn">Edit direction</button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <DirectionChipGroup label="Roles" values={formData.target_roles} emptyText="Add roles" testid="strip-roles" />
        <DirectionChipGroup label="Markets" values={formData.target_locations} emptyText="Add markets" testid="strip-markets" />
        <DirectionChipGroup label="Level" values={formData.target_seniority_levels?.length ? formData.target_seniority_levels : formData.target_seniority} emptyText="Optional" testid="strip-level" />
        <DirectionChipGroup label="Employment types" values={agent.employmentTypes || []} emptyText="Optional" testid="strip-employment-types" />
        <DirectionChipGroup label="Work setup" values={agent.workSetup || []} emptyText="Optional" testid="strip-work-setup" />
        <DirectionChipGroup label="Relocation" values={agent.openToRelocateEurope ? ['Open to relocate in Europe'] : []} emptyText="Optional" testid="strip-relocation" />
      </div>
    </section>
  );
}

function StepShell({ step, title, purpose, children, testid }) {
  const Icon = findJobsSteps.find((item) => item.id === step)?.icon || Compass;
  return (
    <section className="animate-fade-in-up rounded-[2rem] border border-[#d8c8ab] bg-[#fff9ee]/80 p-5 shadow-sm md:p-7" data-testid={testid}>
      <div className="mb-6 flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#d8c8ab] bg-white/55 text-[#173d35]">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-[#7b694d]">Step {step} of {TOTAL_FIND_JOBS_STEPS}</p>
          <h2 className="mt-1 font-serif-display text-2xl font-medium tracking-tight text-[#172820] md:text-3xl" data-testid={`${testid}-title`}>{title}</h2>
          {purpose && <p className="mt-2 text-sm leading-relaxed text-[#6f6657] md:text-base" data-testid={`${testid}-purpose`}>{purpose}</p>}
        </div>
      </div>
      <div className="space-y-6">{children}</div>
    </section>
  );
}

function FieldBlock({ label, htmlFor, helper, error, children, testid }) {
  const errorId = error ? `${testid}-error` : undefined;
  return (
    <div className="space-y-2" data-testid={testid}>
      {label && <Label htmlFor={htmlFor} className="text-base text-[#22362f]">{label}</Label>}
      {children}
      {helper && <p className="text-sm leading-relaxed text-[#6f6657]" data-testid={`${testid}-helper`}>{helper}</p>}
      {error && <p id={errorId} className="text-sm text-red-500" data-testid={`${testid}-error`}>{safeFindJobsText(error)}</p>}
    </div>
  );
}

function optionValue(option) {
  return option && typeof option === 'object' ? option.value : option;
}

function optionLabel(option) {
  return option && typeof option === 'object' ? option.label : option;
}

function optionTestId(option) {
  return String(optionValue(option) || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

function SingleOptionGrid({ options, selected, onSelect, testidPrefix }) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="group" aria-label={testidPrefix}>
      {options.map((option) => {
        const value = optionValue(option);
        const label = optionLabel(option);
        const isSelected = selected === value;
        return (
          <button
            key={value}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onSelect(isSelected ? '' : value)}
            className={`flex min-h-[44px] items-center justify-between gap-2 rounded-xl border p-3 text-left text-sm ${isSelected ? 'border-[#173d35] bg-[#e7f0e9]' : 'border-[#8b7652] bg-white/45 hover:bg-white'}`}
            data-testid={`${testidPrefix}-${optionTestId(option)}`}
          >
            <span>{label}</span>
            {isSelected && <CheckCircle className="h-4 w-4" aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
}

function LocalOptionGrid({ options, selected, onToggle, max = Infinity, testidPrefix }) {
  const selectedValues = uniqueArray(selected || []);
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="group" aria-label={testidPrefix}>
      {options.map((option) => {
        const isSelected = selectedValues.includes(option);
        const atLimit = selectedValues.length >= max && !isSelected;
        return (
          <button
            key={option}
            type="button"
            aria-pressed={isSelected}
            aria-disabled={atLimit}
            disabled={atLimit}
            onClick={() => { if (!atLimit) onToggle(option); }}
            className={`flex min-h-[44px] items-center justify-between gap-2 rounded-xl border p-3 text-left text-sm ${isSelected ? 'border-[#173d35] bg-[#e7f0e9]' : 'border-[#8b7652] bg-white/45 hover:bg-white'} ${atLimit ? 'cursor-not-allowed opacity-55 hover:bg-white/45' : ''}`}
            data-testid={`${testidPrefix}-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          >
            <span>{option}</span>
            {isSelected && <CheckCircle className="h-4 w-4" aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
}

function SelectablePillRow({ options, selected, onToggle, testidPrefix }) {
  const selectedValues = uniqueArray(selected || []);
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={testidPrefix}>
      {options.map((option) => {
        const isSelected = selectedValues.includes(option);
        return (
          <button key={option} type="button" aria-pressed={isSelected} onClick={() => onToggle(option)} className={`inline-flex min-h-[44px] items-center gap-2 rounded-full border px-3 py-2 text-sm ${isSelected ? 'border-[#173d35] bg-[#173d35] text-[#fff8ed]' : 'border-[#8b7652] bg-white/45 text-[#30473e]'}`} data-testid={`${testidPrefix}-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            {isSelected && <CheckCircle className="h-3.5 w-3.5" aria-hidden="true" />}{option}
          </button>
        );
      })}
    </div>
  );
}

function syncDraftWithForm(draft, formData) {
  const base = createCandidateIntakeDraft(draft || {});
  return createCandidateIntakeDraft({
    ...base,
    searchPreferences: {
      ...base.searchPreferences,
      targetRoles: splitList(formData.target_roles),
      targetLocations: splitList(formData.target_locations),
      seniorityLevels: uniqueArray(formData.target_seniority_levels?.length ? formData.target_seniority_levels : splitList(formData.target_seniority)),
      currentJobTitle: formData.job_title || '',
      currentLocation: formData.location || '',
      yearsExperience: formData.years_experience_band || formData.total_experience || '',
      salaryExpectation: formatSalaryFloorsByMarket(formData.salaryFloorsByMarket, activeSalaryMarkets(formData)),
      salaryFloorsByMarket: normalizeSalaryFloorsByMarket(formData.salaryFloorsByMarket, activeSalaryMarkets(formData)),
      visaStatus: formData.visa_status || '',
      germanLevel: hasGermanRelevantMarket(formData) ? (formData.german_level || '') : '',
      otherLanguages: normalizeLanguageRows(formData.languageRows || formData.languages)
        .map((row) => row.proficiency ? `${row.language} (${row.proficiency})` : row.language),
      skills: splitList(formData.core_skills),
      education: formData.education || '',
      educationStatus: formData.education_status || '',
    },
    candidateInsights: {
      ...base.candidateInsights,
      reasonForLooking: formData.reason_for_looking || '',
      availability: formData.availability || '',
      noticePeriod: formData.notice_period || '',
      waysOfWorking: uniqueArray(formData.waysOfWorking || []),
      thingsToAvoid: uniqueArray(formData.thingsToAvoid || []),
      firmNoItems: uniqueArray(formData.firmNoItems || []),
    },
    userEdits: {
      ...base.userEdits,
      findJobsUpdatedAt: new Date().toISOString(),
    },
  });
}

function requiredSubmitErrors({ formData, cvFile, consentGiven, requireDirection = true }) {
  const newErrors = {};
  if (requireDirection && !formData.target_roles.trim()) newErrors.target_roles = 'Target role is required';
  if (requireDirection && !formData.target_locations.trim()) newErrors.target_locations = 'Target location is required';
  if (!formData.name.trim()) newErrors.name = 'Name is required';
  if (!cvFile) newErrors.cv = 'Please upload your CV';
  Object.assign(newErrors, salaryFloorErrors(formData));
  if (!consentGiven) newErrors.consent = 'Please agree to the processing described above to continue.';
  return newErrors;
}

function savedFormHasRequiredDirection(savedFormData) {
  return (
    splitList(savedFormData?.target_roles).length > 0
    && splitList(savedFormData?.target_locations).length > 0
  );
}

function applyCandidatePrefillToState(parsed, setCareerAgentPrefill, setCandidateIntakeDraft, setFormData, setIsDirectionEditing) {
  if (!parsed) return false;
  try {
    const normalized = parseCandidateIntakePrefill(parsed);
    const mapped = normalized.mapped || {};
    const supportedPrefill = {
      target_roles: joinList(mapped.target_roles_list || splitList(mapped.target_roles)),
      target_roles_list: splitList(mapped.target_roles_list || mapped.target_roles),
      target_locations: joinList(mapped.target_locations_list || splitList(mapped.target_locations)),
      target_locations_list: splitList(mapped.target_locations_list || mapped.target_locations),
      target_seniority: typeof mapped.target_seniority === 'string' ? mapped.target_seniority : '',
      target_seniority_levels: Array.isArray(mapped.target_seniority_levels) ? mapped.target_seniority_levels : splitList(mapped.target_seniority),
    };
    const hasSupportedPrefill = Boolean(
      supportedPrefill.target_roles
      || supportedPrefill.target_locations
      || supportedPrefill.target_seniority
      || supportedPrefill.target_seniority_levels.length,
    );
    if (!hasSupportedPrefill) return false;
    setIsDirectionEditing(false);
    setCareerAgentPrefill(supportedPrefill);
    setCandidateIntakeDraft(normalized.candidateIntakeDraft || null);
    setFormData((prev) => ({
      ...prev,
      target_roles: supportedPrefill.target_roles || prev.target_roles,
      target_locations: supportedPrefill.target_locations || prev.target_locations,
      target_seniority: supportedPrefill.target_seniority || prev.target_seniority,
      target_seniority_levels: supportedPrefill.target_seniority_levels.length ? supportedPrefill.target_seniority_levels : prev.target_seniority_levels,
    }));
    return true;
  } catch (_) {
    return false;
  }
}

function safeGetStorage(type) {
  try {
    return window[type];
  } catch (_) {
    return null;
  }
}

function readPrefillFrom(type, key) {
  const storage = safeGetStorage(type);
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    try { storage.removeItem(LEGACY_PHASE_1B_PREFILL_KEY); } catch (_) { return parsed; }
    try { storage.removeItem(CANDIDATE_INTAKE_PREFILL_KEY); } catch (_) { return parsed; }
    return parsed;
  } catch (_) {
    return null;
  }
}

function readCandidatePrefill() {
  if (typeof window === 'undefined') return null;
  return readPrefillFrom('sessionStorage', CANDIDATE_INTAKE_PREFILL_KEY)
    || readPrefillFrom('localStorage', CANDIDATE_INTAKE_PREFILL_KEY)
    || readPrefillFrom('sessionStorage', LEGACY_PHASE_1B_PREFILL_KEY)
    || readPrefillFrom('localStorage', LEGACY_PHASE_1B_PREFILL_KEY);
}

function peekCandidatePrefill() {
  if (typeof window === 'undefined') return null;
  const keys = [CANDIDATE_INTAKE_PREFILL_KEY, LEGACY_PHASE_1B_PREFILL_KEY];
  for (const type of ['sessionStorage', 'localStorage']) {
    const storage = safeGetStorage(type);
    if (!storage) continue;
    for (const key of keys) {
      try {
        const raw = storage.getItem(key);
        if (raw) return JSON.parse(raw);
      } catch (_) {
        return null;
      }
    }
  }
  return null;
}

function maybeJunkShortValue(value) {
  const text = String(value || '').trim();
  if (!text || text.length > 4) return false;
  if (/^\d+$/.test(text)) return false;
  if (/^[a-z]{1,4}$/i.test(text) && !/^(hr|qa|ux|ui|it)$/i.test(text)) return true;
  return false;
}

function focusFirstError(errors) {
  const first = Object.keys(errors || {})[0];
  const idMap = {
    target_roles: 'target_roles',
    target_locations: 'target_locations',
    name: 'name',
    cv: 'cv-upload-control',
    salary_amount: 'salary_amount',
    salary_currency: 'salary_currency',
    salary_period: 'salary_period',
    consent: 'consent',
  };
  const selector = idMap[first] ? `#${idMap[first]}` : `[data-testid="${first}-field"]`;
  window.setTimeout(() => {
    const element = document.querySelector(selector);
    if (element && typeof element.focus === 'function') element.focus();
  }, 0);
}

export default function FindJobsPage() {
  const { user, signInWithOtp, verifyOtp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMasterProfileSetup = searchParams.get('mode') !== null || window.location.pathname === '/master-profile/setup';
  // Continuity through Master Profile accepts only an explicit URL value. It
  // intentionally never revives a stale selected-job session entry here.
  const profileContinuationJobId = isMasterProfileSetup ? (searchParams.get('job_id') || '').trim() : '';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(user ? (isMasterProfileSetup ? 2 : 1) : 0);

  // Auth step state (step 0)
  const [authEmail, setAuthEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [authPhase, setAuthPhase] = useState('main'); // 'main', 'email', 'otp'
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [selectedStoredJobId, setSelectedStoredJobId] = useState(() => readSelectedStoredJobId());
  // This route is evidence setup only. A retained job may be used for the
  // post-confirmation return path, but fit is owned exclusively by CheckFitPage.
  const isExactJobFlow = Boolean(selectedStoredJobId) && !isMasterProfileSetup;

  useEffect(() => {
    if (!user?.id || selectedStoredJobId) return undefined;
    let active = true;
    const resumeCompletedCandidate = async () => {
      try {
        const headers = await getAuthHeaders();
        const response = await fetch(`${API}/submissions/saved-cvs?limit=1`, { headers });
        const body = response.ok ? await response.json() : null;
        const existingSubmissionId = String(body?.items?.[0]?.submission_id || '').trim();
        // Career Profile links here explicitly to review an existing Master CV.
        // That intent must open the existing confirmed/needs-review submission
        // directly, not bounce through the generic returning-candidate redirect.
        if (active && (isCareerProfileResumeRoute() || isMasterProfileSetup) && existingSubmissionId) {
          setDraftSubmissionId(existingSubmissionId);
          return;
        }
        const destination = resolveReturningCandidateRoute({
          loaded: response.ok,
          cvReviewStatus: body?.items?.[0]?.cv_review_status,
          selectedJobId: selectedStoredJobId,
        });
        if (active && destination) navigate(destination, { replace: true });
      } catch (_) { /* Preserve the current resumable flow if this check fails. */ }
    };
    void resumeCompletedCandidate();
    return () => { active = false; };
  }, [user?.id, selectedStoredJobId, navigate, isMasterProfileSetup]);

  const [formData, setFormData] = useState({
    name: '',
    job_title: '',
    location: '',
    target_roles: '',
    target_locations: '',
    target_seniority: '',
    target_seniority_levels: [],
    total_experience: '',
    years_experience_band: '',
    german_level: '',
    languages: '',
    languageRows: [],
    visa_status: '',
    salaryFloorsByMarket: {},
    education_status: '',
    core_skills: '',
    education: '',
    reason_for_looking: '',
    availability: '',
    notice_period: '',
    waysOfWorking: [],
    thingsToAvoid: [],
    firmNoItems: [],
  });
  const [cvFile, setCvFile] = useState(null);
  const [consentGiven, setConsentGiven] = useState(false);
  const [errors, setErrors] = useState({});
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [searchLimitReached, setSearchLimitReached] = useState(false);
  const [searchLimitMessage, setSearchLimitMessage] = useState('');
  // Master CV review flow: after create-draft, the user reviews their canonical CV
  // before we kick off Workflow 0. setDraftSubmissionId(uuid) flips us into review mode.
  const [draftSubmissionId, setDraftSubmissionId] = useState(null);
  const [exactJobResult, setExactJobResult] = useState(null);
  const [exactJobLoading, setExactJobLoading] = useState(false);
  // Synchronous guard against double-submit: React state updates aren't
  // guaranteed to re-render (and disable the trigger button) before a second
  // click in the same tick reaches this handler, so a plain ref is used here
  // instead of relying on exactJobLoading alone.
  const exactJobInFlightRef = useRef(false);
  const [exactJobError, setExactJobError] = useState('');
  const [refinementAnswers, setRefinementAnswers] = useState({});
  const [formAutosaveState, setFormAutosaveState] = useState('clean'); // clean|saving|saved|local
  const [resumeMessage, setResumeMessage] = useState('');
  const [careerAgentPrefill, setCareerAgentPrefill] = useState(null);
  const [candidateIntakeDraft, setCandidateIntakeDraft] = useState(null);
  const [isDirectionEditing, setIsDirectionEditing] = useState(false);
  const careerAgentPrefillAppliedRef = useRef(false);
  const formHydratedRef = useRef(false);
  const formAutosaveTimerRef = useRef(null);
  const cvInputRef = useRef(null);
  const formSnapshot = useMemo(() => JSON.stringify({ formData, consentGiven, currentStep, draftSubmissionId, selectedStoredJobId }), [formData, consentGiven, currentStep, draftSubmissionId, selectedStoredJobId]);
  const hasValidDraftSubmissionId = typeof draftSubmissionId === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(draftSubmissionId);
  const shouldShowExactJobFit = Boolean(
    selectedStoredJobId &&
    (exactJobLoading || exactJobResult || exactJobError)
  );

  useEffect(() => {
    if (careerAgentPrefillAppliedRef.current) return;
    const parsed = peekCandidatePrefill();
    if (!parsed) return;
    if (applyCandidatePrefillToState(parsed, setCareerAgentPrefill, setCandidateIntakeDraft, setFormData, setIsDirectionEditing)) {
      careerAgentPrefillAppliedRef.current = true;
      if (!user?.id) setCurrentStep(0);
    }
  }, [user?.id]);

  // A selected real job is already the candidate's direction. Keep legacy
  // profile preferences out of the exact-fit path until after the result.
  useEffect(() => {
    if (user?.id && isExactJobFlow) setCurrentStep(2);
  }, [user?.id, isExactJobFlow]);

  useEffect(() => {
    if (!user?.id) return;
    if (careerAgentPrefillAppliedRef.current) {
      readCandidatePrefill();
      setCurrentStep((step) => (step === 0 || step === 1 ? 2 : step));
    } else {
      setCurrentStep((step) => (step === 0 ? 1 : step));
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || formHydratedRef.current) return;
    const saved = readFindJobsDraft(user.id);
    if (saved?.form_data && typeof saved.form_data === 'object') {
      setFormData((prev) => ({
        ...prev,
        ...saved.form_data,
        languageRows: normalizeLanguageRows(saved.form_data.languageRows || saved.form_data.languages),
        target_roles: careerAgentPrefillAppliedRef.current ? prev.target_roles : saved.form_data.target_roles,
        target_locations: careerAgentPrefillAppliedRef.current ? prev.target_locations : saved.form_data.target_locations,
        target_seniority: careerAgentPrefillAppliedRef.current ? prev.target_seniority : saved.form_data.target_seniority,
        target_seniority_levels: careerAgentPrefillAppliedRef.current
          ? prev.target_seniority_levels
          : (Array.isArray(saved.form_data.target_seniority_levels) ? saved.form_data.target_seniority_levels : []),
        waysOfWorking: Array.isArray(saved.form_data.waysOfWorking) ? saved.form_data.waysOfWorking : [],
        thingsToAvoid: Array.isArray(saved.form_data.thingsToAvoid) ? saved.form_data.thingsToAvoid : [],
        firmNoItems: Array.isArray(saved.form_data.firmNoItems) ? saved.form_data.firmNoItems : [],
      }));
      setConsentGiven(Boolean(saved.consent_given));
      if (!selectedStoredJobId && typeof saved.selected_stored_job_id === 'string' && saved.selected_stored_job_id.trim()) {
        const restoredJobId = saved.selected_stored_job_id.trim();
        setSelectedStoredJobId(restoredJobId);
        try { window.sessionStorage.setItem(CHECK_FIT_JOB_KEY, restoredJobId); } catch (_) { /* best-effort existing selected-job state */ }
      }
      const prefillHasRequiredDirection = careerAgentPrefillAppliedRef.current && savedFormHasRequiredDirection(careerAgentPrefill);
      const savedHasRequiredDirection = savedFormHasRequiredDirection(saved.form_data);
      setCurrentStep(prefillHasRequiredDirection || savedHasRequiredDirection ? 2 : 1);
      setResumeMessage('Saved form restored. Please reattach your CV file before creating a draft.');
      setFormAutosaveState('saved');
    }
    formHydratedRef.current = true;
  }, [user?.id, careerAgentPrefill, selectedStoredJobId]);

  useEffect(() => {
    if (!user?.id || !formHydratedRef.current || isSubmitted) return undefined;
    setFormAutosaveState('saving');
    if (formAutosaveTimerRef.current) window.clearTimeout(formAutosaveTimerRef.current);
    formAutosaveTimerRef.current = window.setTimeout(() => {
      const ok = writeFindJobsDraft(user.id, formData, consentGiven, currentStep, draftSubmissionId, selectedStoredJobId);
      setFormAutosaveState(ok ? 'saved' : 'local');
    }, FIND_JOBS_AUTOSAVE_DELAY_MS);
    return () => {
      if (formAutosaveTimerRef.current) window.clearTimeout(formAutosaveTimerRef.current);
    };
  }, [user?.id, formSnapshot, formData, consentGiven, currentStep, draftSubmissionId, selectedStoredJobId, isSubmitted]);

  useEffect(() => {
    if (!draftSubmissionId || hasValidDraftSubmissionId || isSubmitted) return;
    setDraftSubmissionId(null);
    setCurrentStep(2);
    setResumeMessage('Create a new draft from your CV before opening Master CV Review.');
  }, [draftSubmissionId, hasValidDraftSubmissionId, isSubmitted]);

  useEffect(() => {
    setCandidateIntakeDraft((prev) => syncDraftWithForm(prev, formData));
  }, [formData]);

  const validateStep = (step) => {
    const newErrors = {};
    if (step === 1 && !isExactJobFlow) {
      if (!formData.target_roles.trim()) newErrors.target_roles = 'Target role is required';
      if (!formData.target_locations.trim()) newErrors.target_locations = 'Target location is required';
    }
    if (step === 2) {
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      if (!cvFile) newErrors.cv = 'Please upload your CV';
    }
    if (step === 10) {
      if (!consentGiven) newErrors.consent = 'Please agree to the processing described above to continue.';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length) focusFirstError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'visa_status') setLiveAnnouncement('Work authorisation updated');
    if (name === 'reason_for_looking') setLiveAnnouncement('Reason for looking updated');
    if (name === 'availability') setLiveAnnouncement('Availability updated');
    if (name === 'notice_period') setLiveAnnouncement('Notice period updated');
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSalaryFloorChange = (market, name, value) => {
    setFormData((prev) => {
      const markets = activeSalaryMarkets(prev);
      const current = normalizeSalaryFloorsByMarket(prev.salaryFloorsByMarket, markets);
      const nextForMarket = salaryFloorForMarket(market, current[market]);
      const salaryFloorsByMarket = {
        ...current,
        [market]: { ...nextForMarket, [name]: value },
      };
      return { ...prev, salaryFloorsByMarket };
    });
    const key = market.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    ['amount', 'currency', 'period'].forEach((part) => {
      const errorKey = `salary_${key}_${part}`;
      if (errors[errorKey]) setErrors((prev) => ({ ...prev, [errorKey]: '' }));
    });
    if (name === 'amount' && !String(value || '').trim()) setLiveAnnouncement(`${market} salary skipped`);
  };

  const updateLanguageRow = (index, key, value) => {
    setFormData((prev) => {
      const rows = normalizeLanguageRows(prev.languageRows);
      const next = rows.length ? rows : [{ language: '', proficiency: '' }];
      next[index] = { ...(next[index] || { language: '', proficiency: '' }), [key]: value };
      const languageRows = next.filter((row, rowIndex) => rowIndex === index || row.language || row.proficiency);
      return { ...prev, languageRows, languages: formatLanguageRows(languageRows) };
    });
    setLiveAnnouncement(key === 'language' && value ? `${value} language updated` : 'Language details updated');
  };

  const addLanguageRow = () => {
    setFormData((prev) => ({
      ...prev,
      languageRows: [...normalizeLanguageRows(prev.languageRows), { language: '', proficiency: '' }],
    }));
  };

  const removeLanguageRow = (index) => {
    setFormData((prev) => {
      const languageRows = normalizeLanguageRows(prev.languageRows).filter((_, rowIndex) => rowIndex !== index);
      return { ...prev, languageRows, languages: formatLanguageRows(languageRows) };
    });
    setLiveAnnouncement('Language removed');
  };

  const toggleSeniorityLevel = (level) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.target_seniority_levels) ? prev.target_seniority_levels : [];
      const next = current.includes(level) ? current.filter((item) => item !== level) : [...current, level];
      return { ...prev, target_seniority_levels: next, target_seniority: next[0] || '' };
    });
  };

  const selectPrimarySeniorityLevel = (level) => {
    setFormData((prev) => ({
      ...prev,
      target_seniority: level,
      target_seniority_levels: level === 'Not sure yet'
        ? ['Not sure yet']
        : uniqueArray([level, ...(prev.target_seniority_levels || []).filter((item) => item !== 'Not sure yet' && item !== level)]),
    }));
    setLiveAnnouncement(`${level} level selected`);
  };

  const toggleNearbySeniorityLevel = (level) => {
    if (!level || level === formData.target_seniority || level === 'Not sure yet') return;
    setFormData((prev) => {
      const nearby = (prev.target_seniority_levels || []).filter((item) => item !== prev.target_seniority && item !== 'Not sure yet');
      const nextNearby = nearby.includes(level) ? nearby.filter((item) => item !== level) : [...nearby, level];
      return { ...prev, target_seniority_levels: uniqueArray([prev.target_seniority, ...nextNearby].filter(Boolean)) };
    });
  };

  const toggleLocalArrayField = (name, value, max = Infinity) => {
    setFormData((prev) => {
      const current = uniqueArray(prev[name] || []);
      const selected = current.includes(value);
      const exclusiveValue = name === 'waysOfWorking'
        ? 'No strong preference'
        : name === 'thingsToAvoid'
          ? 'Nothing to avoid'
          : '';
      let next;
      if (selected) {
        next = current.filter((item) => item !== value);
      } else if (exclusiveValue && value === exclusiveValue) {
        next = [exclusiveValue];
      } else {
        next = uniqueArray([...current.filter((item) => item !== exclusiveValue), value]).slice(0, max);
      }
      const patch = { [name]: next };
      if (name === 'thingsToAvoid') {
        patch.firmNoItems = uniqueArray(prev.firmNoItems || []).filter((item) => next.includes(item));
      }
      return { ...prev, ...patch };
    });
    setLiveAnnouncement(`${value} updated`);
  };

  const toggleFirmNoItem = (value) => {
    setFormData((prev) => ({ ...prev, firmNoItems: uniqueArray(prev.firmNoItems || []).includes(value)
      ? uniqueArray(prev.firmNoItems || []).filter((item) => item !== value)
      : uniqueArray([...(prev.firmNoItems || []), value]) }));
    setLiveAnnouncement('Firm no preference updated');
  };

  const toggleEmploymentType = (type) => {
    setCandidateIntakeDraft((prev) => {
      const base = createCandidateIntakeDraft(prev || {});
      const current = base.careerAgent.employmentTypes || [];
      const employmentTypes = current.includes(type) ? current.filter((item) => item !== type) : [...current, type];
      return createCandidateIntakeDraft({
        ...base,
        careerAgent: { ...base.careerAgent, employmentTypes },
      });
    });
    setLiveAnnouncement(`${type} employment type updated`);
  };

  const handleDirectionTextChange = (name, value) => {
    const clean = joinList(splitList(value));
    setFormData((prev) => ({ ...prev, [name]: clean }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // Auth Step (Step 0)

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setErrors({});
    try {
      createPendingIntent({ kind: 'auth_return', return_path: '/find-jobs/check-fit' });
      const { error } = await signInWithGoogle(authCallbackUrl());
      if (error) throw error;
    } catch (err) {
      setErrors({ auth: safeFindJobsText(err) || 'Failed to start Google sign-in' });
      setIsGoogleLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!authEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(authEmail)) {
      setErrors({ auth: 'Please enter a valid email' });
      return;
    }
    setIsSendingOtp(true);
    setErrors({});
    try {
      createPendingIntent({ kind: 'auth_return', return_path: '/find-jobs/check-fit' });
      const { error } = await signInWithOtp(authEmail.toLowerCase().trim(), authCallbackUrl());
      if (error) throw error;
      setAuthPhase('otp');
    } catch (err) {
      setErrors({ auth: safeFindJobsText(err) || 'Failed to send verification code' });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim() || otpCode.length !== 6) {
      setErrors({ auth: 'Please enter the 6-digit code' });
      return;
    }
    setIsVerifyingOtp(true);
    setErrors({});
    try {
      const { error } = await verifyOtp(authEmail.toLowerCase().trim(), otpCode.trim());
      if (error) throw error;
      setCurrentStep(1);
    } catch (err) {
      setErrors({ auth: safeFindJobsText(err) || 'Invalid or expired code' });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // File handling
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) validateAndSetFile(file);
  };

  const validateAndSetFile = (file) => {
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({ ...prev, cv: 'Only PDF and DOCX files are allowed' }));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, cv: 'File size must be less than 10MB' }));
      return;
    }
    setCvFile(file);
    setLiveAnnouncement('CV added');
    setErrors((prev) => ({ ...prev, cv: '' }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) validateAndSetFile(e.dataTransfer.files[0]);
  };

  const hasPrefill = hasCareerAgentPrefill(careerAgentPrefill);
  const hasValidPrefillDirection = Boolean(hasPrefill && formData.target_roles.trim() && formData.target_locations.trim());
  const effectiveCurrentStep = user && currentStep === 0
    ? (isExactJobFlow ? 2 : 1)
    : (hasValidPrefillDirection && currentStep === 1 && !isDirectionEditing ? 2 : currentStep);

  const handleNext = () => {
    if (isExactJobFlow && effectiveCurrentStep === 2) {
      if (!validateStep(2)) return;
      if (!consentGiven) {
        setErrors((prev) => ({ ...prev, consent: 'Please agree to the processing described above to continue.' }));
        return;
      }
      handleSubmit({ preventDefault: () => {} });
      return;
    }
    if (validateStep(effectiveCurrentStep)) setCurrentStep((prev) => Math.min((user && prev === 0 ? 1 : prev) + 1, TOTAL_FIND_JOBS_STEPS));
  };

  const handleBack = () => {
    if (isExactJobFlow && effectiveCurrentStep === 2) return;
    if (hasValidPrefillDirection && effectiveCurrentStep === 2 && !isDirectionEditing) {
      setCurrentStep(1);
      setIsDirectionEditing(true);
      return;
    }
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalErrors = requiredSubmitErrors({ formData, cvFile, consentGiven, requireDirection: !isExactJobFlow });
    if (Object.keys(finalErrors).length) {
      setErrors(finalErrors);
      if (finalErrors.target_roles || finalErrors.target_locations) setCurrentStep(1);
      else if (finalErrors.name || finalErrors.cv) setCurrentStep(2);
      else if (Object.keys(finalErrors).some((key) => key.startsWith('salary_'))) setCurrentStep(9);
      else setCurrentStep(10);
      focusFirstError(finalErrors);
      return;
    }
    setIsSubmitting(true);

    try {
      const headers = await getAuthHeaders();
      const submitData = new FormData();
      const languagesForSubmit = formatLanguageRows(formData.languageRows || formData.languages);
      const germanLevelForSubmit = hasGermanRelevantMarket(formData) ? formData.german_level : '';
      submitData.append('name', formData.name);
      submitData.append('cv_file', cvFile);
      submitData.append('job_title', formData.job_title);
      submitData.append('location', formData.location);
      submitData.append('target_roles', formData.target_roles);
      submitData.append('target_locations', formData.target_locations);
      submitData.append('target_seniority', formData.target_seniority);
      submitData.append('target_seniority_levels', JSON.stringify(formData.target_seniority_levels || []));
      submitData.append('total_experience', formData.total_experience);
      submitData.append('years_experience_band', formData.years_experience_band);
      submitData.append('german_level', germanLevelForSubmit);
      submitData.append('languages', languagesForSubmit);
      submitData.append('visa_status', formData.visa_status);
      // Market-specific salary floors remain browser-draft only in this frontend phase.
      // Do not collapse multiple market values into the legacy scalar salary_expectation field.
      submitData.append('core_skills', formData.core_skills);
      submitData.append('education', formData.education);
      submitData.append('consent', consentGiven);

      const resp = await axios.post(`${API}/submissions/create-draft`, submitData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      const newDraftId = resp.data?.submission_id;
      if (!newDraftId) throw new Error('Could not create draft submission.');
      writeFindJobsDraft(user.id, formData, consentGiven, currentStep, newDraftId, selectedStoredJobId);
      setDraftSubmissionId(newDraftId);
    } catch (error) {
      console.error('Submission error:', error);
      const status = error.response?.status;
      const data = error.response?.data || error;
      const code = getApiErrorCode(data);
      if (code === 'search_limit_reached' || status === 429) {
        setSearchLimitMessage(
          getSafeErrorMessage(data, { context: 'find-jobs-submit' }) ||
          'You already have a recent search. Please wait before starting another one.'
        );
        setSearchLimitReached(true);
      } else {
        const message = getSafeErrorMessage(data || error, { context: 'find-jobs-submit' });
        setErrors({ submit: message });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMasterProfileUpload = async (e) => {
    e.preventDefault();
    const uploadErrors = {};
    if (!formData.name.trim()) uploadErrors.name = 'Name is required';
    if (!cvFile) uploadErrors.cv = 'Please upload your CV';
    if (Object.keys(uploadErrors).length) {
      setErrors(uploadErrors);
      focusFirstError(uploadErrors);
      return;
    }
    setIsSubmitting(true);
    setErrors({});
    try {
      const headers = await getAuthHeaders();
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('cv_file', cvFile);
      submitData.append('consent', 'true');
      const resp = await axios.post(`${API}/submissions/create-draft`, submitData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      const newDraftId = resp.data?.submission_id;
      if (!newDraftId) throw new Error('Could not create profile draft.');
      setDraftSubmissionId(newDraftId);
    } catch (error) {
      setErrors({ submit: getSafeErrorMessage(error.response?.data || error, { context: 'find-jobs-submit' }) || 'We could not prepare your profile. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToFormFromReview = useCallback(() => {
    setDraftSubmissionId(null);
    setCurrentStep(2);
    setResumeMessage('Create a new draft from your CV before opening Master CV Review.');
  }, []);

  const handleMissingDraft = useCallback(() => {
    setDraftSubmissionId(null);
    setCurrentStep(2);
    setResumeMessage('We could not find that CV draft. Please create it again.');
  }, []);

  // Unguarded core: only called from within an already-locked in-flight
  // section (either startExactJobAnalysis below, or the stale-context
  // fallback inside submitRefinement), so it must not touch the ref/loading
  // state itself.
  const runExactJobAnalysis = useCallback(async (submissionId) => {
    if (!shouldRunExactJobAnalysis(selectedStoredJobId, submissionId)) return undefined;
    setExactJobError('');
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API}/agent/analyze-stored-job`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(buildExactJobRequest(selectedStoredJobId, submissionId)) });
      const body = await response.json();
      if (!response.ok) throw body;
      setExactJobResult(body); setRefinementAnswers({});
      return body;
    } catch (error) {
      const message = getSafeErrorMessage(error, { context: 'find-jobs-submit' }) || 'We could not refresh this fit result. Please try again.';
      setExactJobError(message);
      return { success: false, message };
    }
  }, [selectedStoredJobId]);

  const startExactJobAnalysis = useCallback(async (submissionId) => {
    if (exactJobInFlightRef.current) return undefined;
    exactJobInFlightRef.current = true;
    setExactJobLoading(true);
    try {
      return await runExactJobAnalysis(submissionId);
    } finally {
      exactJobInFlightRef.current = false;
      setExactJobLoading(false);
    }
  }, [runExactJobAnalysis]);

  const submitRefinement = useCallback(async () => {
    if (!exactJobResult?.refinement_context_id || !exactJobResult?.submission_id) return;
    const prompts = (exactJobResult.refinement_prompts || []).slice(0, 2);
    const answers = prompts.filter((prompt) => refinementAnswers[prompt.refinement_id]).map((prompt) => ({ refinement_id: prompt.refinement_id, value: refinementAnswers[prompt.refinement_id] }));
    if (!answers.length) { setExactJobError('Choose an answer before continuing.'); return; }
    if (exactJobInFlightRef.current) return;
    exactJobInFlightRef.current = true;
    setExactJobLoading(true); setExactJobError('');
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API}/agent/analyze-stored-job/refine`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(buildRefinementRequest(selectedStoredJobId, exactJobResult, answers)) });
      const body = await response.json();
      if (!response.ok) {
        if (getApiErrorCode(body) === 'stale_refinement_context') { await runExactJobAnalysis(exactJobResult.submission_id); return; }
        throw body;
      }
      setExactJobResult(body); setRefinementAnswers({});
    } catch (error) { setExactJobError(getSafeErrorMessage(error, { context: 'find-jobs-submit' }) || 'We could not update this fit result. Please try again.'); }
    finally { exactJobInFlightRef.current = false; setExactJobLoading(false); }
  }, [exactJobResult, refinementAnswers, selectedStoredJobId, runExactJobAnalysis]);

  if (hasValidDraftSubmissionId && !isSubmitted && !shouldShowExactJobFit) {
    return (
      <div className="min-h-screen py-10 md:py-16" data-testid="find-jobs-master-cv-step">
        <MasterCvReview
          submissionId={draftSubmissionId}
          userId={user?.id}
          initialPreferences={formData}
          candidateIntakeDraft={candidateIntakeDraft}
          onStartExactJob={isExactJobFlow ? (({ submissionId }) => startExactJobAnalysis(submissionId)) : undefined}
          onConfirmMasterProfile={isMasterProfileSetup ? (({ submissionId, expectedUpdatedAt }) => confirmMasterProfile(submissionId, expectedUpdatedAt)) : undefined}
          onConfirmed={(result) => {
            clearFindJobsDraft(user?.id);
            if (isMasterProfileSetup) {
              const returnPath = profileContinuationJobId
                ? `/find-jobs/check-fit?job_id=${encodeURIComponent(profileContinuationJobId)}`
                : '/master-profile';
              navigate(returnPath, { replace: true });
              return;
            }
            if (isExactJobFlow) {
              // exactJobResult/exactJobError was already populated by onStartExactJob as part
              // of this same confirm action; the exact-job-fit-flow screen below picks it up.
              // The canonical job identity is not cleared here — only once the final CTA is used.
              return;
            }
            setIsSubmitted(true);
          }}
          onBackToForm={handleBackToFormFromReview}
          onMissingDraft={handleMissingDraft}
        />
      </div>
    );
  }

  if (isMasterProfileSetup) {
    return (
      <main className="min-h-screen bg-[#f6efe3] px-5 py-10 text-[#22362f]" data-testid="master-profile-setup-page">
        <section className="mx-auto max-w-2xl border border-[#d8c8ab] bg-[#fff9ee] p-6 sm:p-8">
          <p className="text-xs uppercase tracking-[0.18em] text-[#7b694d]">Master Profile</p>
          <h1 className="mt-3 font-serif-display text-4xl font-medium">Upload your CV</h1>
          <p className="mt-3 text-sm leading-relaxed text-[#5f6c64]">We’ll extract your profile evidence for you to review and edit before you confirm it.</p>
          {errors.submit && <p className="mt-5 text-sm text-red-700" data-testid="master-profile-setup-error">{safeFindJobsText(errors.submit)}</p>}
          <form className="mt-7 space-y-6" onSubmit={handleMasterProfileUpload} data-testid="master-profile-upload-form">
            <FieldBlock label="Name *" htmlFor="master-profile-name" error={errors.name} testid="name-field">
              <Input id="master-profile-name" name="name" value={formData.name} onChange={handleInputChange} className="form-input" data-testid="master-profile-name-input" />
            </FieldBlock>
            <FieldBlock label="CV *" helper="PDF or DOCX, up to 10 MB." error={errors.cv} testid="cv-field">
              <input ref={cvInputRef} id="cv-upload-control" type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={handleFileChange} data-testid="master-profile-cv-upload" />
              {cvFile && <p className="mt-2 text-sm text-[#30473e]" data-testid="master-profile-selected-cv">{cvFile.name}</p>}
            </FieldBlock>
            <Button type="submit" className="btn-primary" disabled={isSubmitting} data-testid="master-profile-upload-submit">{isSubmitting ? 'Preparing your profile…' : 'Review your Master Profile'}</Button>
          </form>
        </section>
      </main>
    );
  }

  if (searchLimitReached) {
    return (
      <div className="min-h-screen flex items-center justify-center py-24" data-testid="search-limit-page">
        <div className="max-w-lg mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-foreground/5 border border-foreground/20 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-foreground" />
          </div>
          <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight font-medium mb-4">
            One search at a time
          </h1>
          <p className="text-muted-foreground mb-8 text-lg" data-testid="search-limit-message">
            {safeFindJobsText(searchLimitMessage)}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/dashboard" className="btn-primary inline-flex items-center gap-2" data-testid="search-limit-dashboard-btn">
              Go to Dashboard
            </Link>
            <Link to="/job-results" className="btn-secondary inline-flex items-center gap-2" data-testid="search-limit-results-btn">
              View Job Results
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (shouldShowExactJobFit) {
    const prompts = (exactJobResult?.refinement_prompts || []).slice(0, 2);
    const isFinal = exactJobResult?.result_state === 'final' && !prompts.length;
    return <div className="min-h-screen bg-[#f6efe3] py-12" data-testid="exact-job-fit-flow"><div className="mx-auto max-w-2xl px-5">
      <div className="rounded-3xl border border-[#d8c8ab] bg-white/70 p-7"><p className="text-xs uppercase tracking-[0.18em] text-[#7b694d]">Check my fit</p><h1 className="mt-2 font-serif-display text-3xl text-[#172820]">{exactJobLoading ? 'Checking your fit' : isFinal ? 'Your fit result' : 'One detail can refine this result'}</h1>
      {exactJobLoading && <p className="mt-4 text-sm text-[#6f6657]">Using your confirmed CV and this selected job.</p>}
      {exactJobError && <div className="mt-4" role="alert"><p className="text-sm text-red-700">{safeFindJobsText(exactJobError)}</p><Button className="mt-3 btn-secondary" onClick={() => startExactJobAnalysis(exactJobResult?.submission_id || draftSubmissionId)}>Try again</Button></div>}
      {exactJobResult && <><p className="mt-4 text-lg font-medium text-[#173d35]" data-testid="exact-job-fit">{exactJobResult.display_fit || exactJobResult.fit || 'Fit result'}</p>{isFinal ? <>{['strong', 'medium'].includes(String(exactJobResult.display_fit || exactJobResult.fit || '').toLowerCase()) ? <Link className="mt-5 inline-flex btn-primary" onClick={clearSelectedStoredJobState} to={buildExactJobApplicationRoute(selectedStoredJobId, exactJobResult.job_result_id)}>Prepare my application</Link> : <Link className="mt-5 inline-flex btn-secondary" onClick={clearSelectedStoredJobState} to="/find-jobs">Explore other roles</Link>}</> : <div className="mt-6 space-y-5">{prompts.map((prompt) => <fieldset key={prompt.refinement_id}><legend className="font-medium text-[#172820]">{prompt.reason || 'Please confirm this job-specific detail.'}</legend><div className="mt-3 flex flex-wrap gap-2">{(prompt.type === 'language' ? [['meets_requirement','Meets requirement'],['does_not_meet_requirement','Does not meet requirement'],['unknown','I do not know']] : [['eligible','Eligible for this role'],['ineligible','Not eligible for this role'],['unknown','I do not know']]).map(([value,label]) => <label key={value} className="rounded-lg border border-[#d8c8ab] px-3 py-2 text-sm"><input type="radio" name={prompt.refinement_id} value={value} checked={refinementAnswers[prompt.refinement_id] === value} onChange={() => setRefinementAnswers((current) => ({ ...current, [prompt.refinement_id]: value }))} /> <span className="ml-2">{label}</span></label>)}</div></fieldset>)}<Button className="btn-primary" onClick={submitRefinement} disabled={exactJobLoading}>Update fit result</Button></div>}</>}</div></div></div>;
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center py-24" data-testid="success-page">
        <div className="max-w-lg mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-foreground/5 border border-foreground/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-foreground" />
          </div>
          <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight font-medium mb-4">
            Profile Submitted!
          </h1>
          <p className="text-muted-foreground mb-8 text-lg">
            Thank you, {formData.name}! We&apos;ve received your profile and CV.
            You can track your results on your dashboard.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/dashboard" className="btn-primary inline-flex items-center gap-2" data-testid="go-dashboard-btn">
              Go to Dashboard
            </Link>
            <Link to="/" className="btn-secondary inline-flex items-center gap-2" data-testid="back-home-btn">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const currentStepMeta = findJobsSteps.find((item) => item.id === effectiveCurrentStep) || findJobsSteps[0];
  const progressPercent = user ? (effectiveCurrentStep / TOTAL_FIND_JOBS_STEPS) * 100 : 0;

  return (
    <div className="min-h-screen bg-[#f6efe3] py-8 text-[#22362f] md:py-12">
      <div className="mx-auto max-w-6xl px-5 md:px-10">
        <div className="sr-only" aria-live="polite" aria-atomic="true" data-testid="find-jobs-live-announcements">{liveAnnouncement}</div>
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#6f6657] hover:text-[#173d35] transition-colors mb-6"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#d9c8aa] bg-white/45 px-4 py-2 text-xs uppercase tracking-[0.18em] text-[#6c5d48]" data-testid="find-jobs-continuation-eyebrow">
            {isExactJobFlow ? 'Check My Fit' : (isMasterProfileSetup ? 'Master Profile setup' : 'Job search intake')}
          </span>
          <h1 className="font-serif-display text-3xl md:text-4xl tracking-tight font-medium mb-4 text-[#172820]" data-testid="find-jobs-heading">
            {isExactJobFlow ? 'Start with your CV' : (isMasterProfileSetup ? 'Add or update your Master Profile' : 'Complete your search profile')}
          </h1>
          <p className="max-w-2xl text-[#6f6657] text-sm md:text-base leading-relaxed" data-testid="find-jobs-intro-copy">
            {isExactJobFlow ? 'We will use your selected job and your CV to prepare an honest preliminary fit. You can add preferences after the result.' : (isMasterProfileSetup ? 'Upload a CV to extract your profile evidence, then review and correct it before confirming.' : 'One focused step at a time. We will only ask for details still needed for search and CV review.')}
          </p>
          {user && (
            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground" data-testid="find-jobs-autosave-status">
              <span data-testid="find-jobs-autosave-state">
                {formAutosaveState === 'saving' && 'Saving…'}
                {formAutosaveState === 'saved' && 'Saved'}
                {formAutosaveState === 'local' && 'Saved locally'}
                {formAutosaveState === 'clean' && 'Saved'}
              </span>
              {resumeMessage && <span className="text-emerald-700 dark:text-emerald-300" data-testid="find-jobs-resume-message">{safeFindJobsText(resumeMessage)}</span>}
            </div>
          )}
          {selectedStoredJobId && (
            <div className="mt-4 rounded-2xl border border-[#d8c8ab] bg-[#fff9ee]/80 p-4 text-sm text-[#30473e]" data-testid="selected-stored-job-banner">
              <p className="font-medium text-[#172820]">Selected job retained for fit check</p>
              <p className="mt-1 break-all" data-testid="selected-stored-job-id">job_id: {selectedStoredJobId}</p>
            </div>
          )}
        </div>

        {/* Progress Indicator */}
        {user && !isExactJobFlow && (
          <div className="mb-8 rounded-2xl border border-[#d8c8ab] bg-[#fff9ee]/70 p-4" data-testid="progress-indicator">
            <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-[#7b694d]">{currentStepMeta.title}</p>
              <p className="text-xs text-[#6f6657]">Step {effectiveCurrentStep} of {TOTAL_FIND_JOBS_STEPS}</p>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-[#e5d7bd]" role="progressbar" aria-valuemin="1" aria-valuemax={TOTAL_FIND_JOBS_STEPS} aria-valuenow={effectiveCurrentStep} aria-label={`Find Jobs step ${effectiveCurrentStep} of ${TOTAL_FIND_JOBS_STEPS}`}>
              <div className="h-full rounded-full bg-[#173d35] transition-[width] duration-200 motion-reduce:transition-none" style={{ width: `${progressPercent}%` }} data-testid="find-jobs-progress-bar" />
            </div>
          </div>
        )}
        {user && isExactJobFlow && (
          <OnboardingCard className="mb-8" data-testid="exact-job-milestone-indicator"><OnboardingMilestones active="Experience" /><p className="mt-3 text-sm text-[#30473e]">For this job, we start with CV evidence and review. The remaining profile milestones are available later when they are useful.</p></OnboardingCard>
        )}

        <MobileCareerAgentSummary formData={formData} cvFile={cvFile} candidateIntakeDraft={candidateIntakeDraft} currentStep={effectiveCurrentStep} />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[0.68fr_0.32fr]">
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="client-form">

          {/* Step 0: Auth (only if not signed in) */}
          {effectiveCurrentStep === 0 && !user && (
            <div className="space-y-6 animate-fade-in-up" data-testid="step-auth">
              <div className="p-6 border border-border bg-surface">
                <h3 className="font-medium mb-1">Sign in to get started</h3>
                <p className="text-muted-foreground text-sm mb-5">
                  Create an account or sign in to submit your profile.
                </p>

                <div className="space-y-3">
                  {/* Google, primary */}
                  <Button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={isGoogleLoading}
                    className="w-full h-11 flex items-center justify-center gap-3 border border-border bg-background hover:bg-surface/80 text-foreground font-medium rounded-lg transition-colors"
                    data-testid="google-sign-in-btn"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    {isGoogleLoading ? 'Redirecting...' : 'Continue with Google'}
                  </Button>

                  {/* Divider */}
                  <div className="flex items-center gap-4">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs text-muted-foreground uppercase tracking-wider">or</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>

                  {/* Email, toggle */}
                  {authPhase === 'main' && (
                    <button
                      type="button"
                      onClick={() => setAuthPhase('email')}
                      className="w-full h-11 flex items-center justify-center gap-2 border border-border bg-background hover:bg-surface/80 text-foreground text-sm font-medium rounded-lg transition-colors"
                      data-testid="email-sign-in-toggle"
                    >
                      <Mail className="w-4 h-4" />
                      Sign in with email link
                    </button>
                  )}

                  {authPhase === 'email' && (
                    <div className="space-y-3 pt-1">
                      <div className="space-y-1.5">
                        <Label htmlFor="auth-email" className="text-sm">Email Address *</Label>
                        <Input
                          id="auth-email"
                          type="email"
                          placeholder="your@email.com"
                          value={authEmail}
                          onChange={(e) => setAuthEmail(e.target.value)}
                          className="form-input text-base py-3"
                          data-testid="input-auth-email"
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={isSendingOtp}
                        className="btn-primary w-full"
                        data-testid="send-otp-btn"
                      >
                        {isSendingOtp ? 'Sending...' : 'Send Sign-In Link'}
                      </Button>
                      <button type="button" onClick={() => { setAuthPhase('main'); setAuthEmail(''); setErrors({}); }} className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="find-jobs-auth-back-options">
                        Back to all options
                      </button>
                    </div>
                  )}

                  {authPhase === 'otp' && (
                    <div className="space-y-3 pt-1">
                      <p className="text-muted-foreground text-sm">
                        We sent a sign-in link to <strong>{authEmail}</strong>
                      </p>
                      <div className="p-3 bg-foreground/5 border border-border rounded-md space-y-1">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <ExternalLink className="w-4 h-4" />
                          Click the link in your email to sign in
                        </div>
                        <p className="text-xs text-muted-foreground">You&apos;ll be redirected back here automatically.</p>
                      </div>
                      <div className="border-t border-border pt-3">
                        <p className="text-xs text-muted-foreground mb-2">Or enter the 6-digit code:</p>
                        <div className="flex gap-3">
                          <Input
                            type="text"
                            placeholder="6-digit code"
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className="form-input text-center text-xl tracking-[0.3em] flex-1"
                            maxLength={6}
                            data-testid="input-otp-code"
                          />
                          <Button
                            type="button"
                            onClick={handleVerifyOtp}
                            disabled={isVerifyingOtp || otpCode.length !== 6}
                            className="btn-secondary"
                            data-testid="verify-otp-btn"
                          >
                            {isVerifyingOtp ? '...' : 'Verify'}
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-between text-xs">
                        <button type="button" onClick={() => { setAuthPhase('main'); setOtpCode(''); setErrors({}); }} className="text-muted-foreground hover:text-foreground" data-testid="find-jobs-otp-back-btn">Back</button>
                        <button type="button" onClick={handleSendOtp} disabled={isSendingOtp} className="text-muted-foreground hover:text-foreground" data-testid="find-jobs-otp-resend-btn">Resend</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {errors.auth && <p className="text-sm text-red-500">{safeFindJobsText(errors.auth)}</p>}
            </div>
          )}

          {/* Step 1: Confirm direction */}
          {effectiveCurrentStep === 1 && (
              <StepShell step={1} title="Confirm your direction" purpose={hasPrefill ? 'Your saved direction is ready. Review it before adding CV evidence.' : 'Add the target roles and markets you want this search to focus on.'} testid="step-1-form">
              {user && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2 text-sm" data-testid="signed-in-banner">
                  <Shield className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="text-green-700 dark:text-green-300">Signed in as {user.email}</span>
                </div>
              )}
              {hasPrefill && !isDirectionEditing && (
                <div className="rounded-2xl border border-[#d8c8ab] bg-white/45 p-4 motion-safe:animate-[phase1bQuestionIn_220ms_ease-out_forwards]" data-testid="career-agent-direction-summary">
                  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                    <h3 className="font-serif-display text-xl font-medium text-[#172820]">Saved direction</h3>
                    <p className="mt-1 text-sm text-[#6f6657]">These choices came from your saved setup. You do not need to answer them again unless you want to edit.</p>
                    </div>
                    <button type="button" onClick={() => setIsDirectionEditing(true)} className="inline-flex shrink-0 items-center justify-center rounded-md border border-[#c8b994] bg-[#fff9ee]/80 px-4 py-2 text-sm font-medium text-[#173d35] transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-white" data-testid="edit-career-agent-direction-btn">
                      Edit direction
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <DirectionChipGroup label="Roles" values={careerAgentPrefill?.target_roles_list?.length ? careerAgentPrefill.target_roles_list : formData.target_roles} emptyText="Add roles" testid="direction-summary-roles" />
                    <DirectionChipGroup label="Markets" values={careerAgentPrefill?.target_locations_list?.length ? careerAgentPrefill.target_locations_list : formData.target_locations} emptyText="Add markets" testid="direction-summary-locations" />
                    <DirectionChipGroup label="Level" values={formData.target_seniority_levels?.length ? formData.target_seniority_levels : formData.target_seniority} emptyText="Add level" testid="direction-summary-seniority" />
                    {candidateIntakeDraft?.careerAgent?.workSetup?.length ? <DirectionChipGroup label="Work setup" values={candidateIntakeDraft.careerAgent.workSetup} emptyText="Optional" testid="direction-summary-work-setup" /> : null}
                  </div>
                  {(errors.target_roles || errors.target_locations) && (
                    <div className="mt-4 space-y-1" data-testid="direction-summary-errors">
                      {errors.target_roles && <p className="text-sm text-red-500">{safeFindJobsText(errors.target_roles)}</p>}
                      {errors.target_locations && <p className="text-sm text-red-500">{safeFindJobsText(errors.target_locations)}</p>}
                    </div>
                  )}
                </div>
              )}
              {(!hasPrefill || isDirectionEditing) && (
                <div className="space-y-6" data-testid="career-agent-direction-edit-fields">
                  {hasPrefill && <div className="rounded-2xl border border-[#d8c8ab] bg-white/45 p-4 text-sm text-[#6f6657]" data-testid="prefill-edit-note">Edit your direction here. Your edits will be used for this search.</div>}
                  <FieldBlock label="Target roles *" htmlFor="target_roles" helper="Separate multiple roles with commas." error={errors.target_roles} testid="target-roles-field">
                    <Input id="target_roles" name="target_roles" placeholder="e.g., Product Manager, Project Manager" value={formData.target_roles} onChange={(e) => handleDirectionTextChange('target_roles', e.target.value)} aria-describedby={errors.target_roles ? 'target-roles-field-error' : undefined} className={`form-input text-base py-4 ${errors.target_roles ? 'border-red-500' : ''}`} data-testid="input-target-roles" />
                    <FromCareerAgentHint show={Boolean(careerAgentPrefill?.target_roles)} />
                    <DirectionChipGroup label="Selected roles" values={formData.target_roles} emptyText="Add roles" testid="direction-edit-role-chips" />
                  </FieldBlock>
                  <FieldBlock label="Target markets *" htmlFor="target_locations" helper="Separate multiple markets or cities with commas." error={errors.target_locations} testid="target-locations-field">
                    <Input id="target_locations" name="target_locations" placeholder="e.g., Munich, Berlin, London, Remote Europe" value={formData.target_locations} onChange={(e) => handleDirectionTextChange('target_locations', e.target.value)} aria-describedby={errors.target_locations ? 'target-locations-field-error' : undefined} className={`form-input text-base py-4 ${errors.target_locations ? 'border-red-500' : ''}`} data-testid="input-target-locations" />
                    <FromCareerAgentHint show={Boolean(careerAgentPrefill?.target_locations)} />
                    <DirectionChipGroup label="Selected markets" values={formData.target_locations} emptyText="Add markets" testid="direction-edit-location-chips" />
                  </FieldBlock>
                  <FieldBlock label="What level should your agent prioritise?" testid="target-seniority-field" helper="This helps your agent avoid roles that are clearly too senior or too junior. Associate often means roughly 1 to 3 years, but varies by employer.">
                    <FromCareerAgentHint show={Boolean(careerAgentPrefill?.target_seniority || careerAgentPrefill?.target_seniority_levels?.length)} />
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Priority level" data-testid="seniority-primary-select">
                      {DIRECT_LEVEL_OPTIONS.map((level) => {
                        const selected = formData.target_seniority === level;
                        return (
                          <button key={level} type="button" role="radio" aria-checked={selected} onClick={() => selectPrimarySeniorityLevel(level)} className={`flex min-h-[44px] items-center justify-between gap-2 rounded-xl border p-3 text-left text-sm ${selected ? 'border-[#173d35] bg-[#e7f0e9]' : 'border-[#8b7652] bg-white/45 hover:bg-white'}`} data-testid={`seniority-option-${level.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                            <span>{level}</span>
                            {selected && <CheckCircle className="h-4 w-4" aria-hidden="true" />}
                          </button>
                        );
                      })}
                    </div>
                    {formData.target_seniority && formData.target_seniority !== 'Not sure yet' && (
                      <div className="mt-4 space-y-2">
                        <p className="text-sm font-medium text-[#22362f]">Nearby levels, optional</p>
                        <div className="flex flex-wrap gap-2" aria-label="Nearby levels">
                          {DIRECT_LEVEL_OPTIONS.filter((level) => level !== formData.target_seniority && level !== 'Not sure yet').map((level) => {
                            const selected = formData.target_seniority_levels.includes(level);
                            return <button key={level} type="button" aria-pressed={selected} onClick={() => toggleNearbySeniorityLevel(level)} className={`min-h-[44px] rounded-full border px-3 py-2 text-sm ${selected ? 'border-[#173d35] bg-[#e7f0e9]' : 'border-[#8b7652] bg-white/45'}`} data-testid={`nearby-seniority-option-${level.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{level}{selected && <CheckCircle className="ml-2 inline h-3.5 w-3.5" aria-hidden="true" />}</button>;
                          })}
                        </div>
                      </div>
                    )}
                  </FieldBlock>
                  <FieldBlock label="Which employment types work for you?" testid="employment-types-field" helper="Optional. Pick any that fit your current search.">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" aria-label="Employment types" data-testid="employment-type-select">
                      {EMPLOYMENT_TYPE_OPTIONS.map((type) => {
                        const selected = candidateIntakeDraft?.careerAgent?.employmentTypes?.includes(type);
                        return (
                          <button key={type} type="button" aria-pressed={Boolean(selected)} onClick={() => toggleEmploymentType(type)} className={`flex min-h-[44px] items-center justify-between gap-2 rounded-xl border p-3 text-left text-sm ${selected ? 'border-[#173d35] bg-[#e7f0e9]' : 'border-[#8b7652] bg-white/45 hover:bg-white'}`} data-testid={`employment-type-option-${type.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                            <span>{type}</span>
                            {selected && <CheckCircle className="h-4 w-4" aria-hidden="true" />}
                          </button>
                        );
                      })}
                    </div>
                  </FieldBlock>
                  {hasPrefill && <button type="button" onClick={() => { setIsDirectionEditing(false); setCurrentStep(2); }} className="rounded-md bg-[#173d35] px-5 py-3 text-sm font-medium text-[#fff8ed] transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-[#102f29]" data-testid="save-career-agent-direction-btn">Save direction</button>}
                </div>
              )}
            </StepShell>
          )}

          {/* Step 2: Add CV */}
          {effectiveCurrentStep === 2 && (
            <>
            {hasValidPrefillDirection && !isDirectionEditing && <CareerAgentDirectionStrip formData={formData} candidateIntakeDraft={candidateIntakeDraft} onEdit={() => { setCurrentStep(1); setIsDirectionEditing(true); }} />}
            <StepShell step={2} title="Add evidence your agent can use" purpose="Your agent knows your direction. Now give it evidence it is allowed to use." testid="step-2-form">
              <div className="rounded-2xl border border-[#d8c8ab] bg-white/45 p-4 text-sm leading-relaxed text-[#6f6657]" data-testid="cv-evidence-copy">
                <ul className="list-disc space-y-2 pl-5">
                  <li>We extract information from your CV so you do not have to enter everything manually.</li>
                  <li>You review and correct what we find.</li>
                  <li>Your agent does not invent experience, skills or qualifications.</li>
                </ul>
              </div>
              <FieldBlock label="Name *" htmlFor="name" helper="Use the name you want on your application documents." error={errors.name} testid="name-field">
                <Input id="name" name="name" placeholder="Your full name" value={formData.name} onChange={handleInputChange} aria-describedby={errors.name ? 'name-field-error' : undefined} className={`form-input text-base py-4 ${errors.name ? 'border-red-500' : ''}`} data-testid="input-name" />
              </FieldBlock>
              <FieldBlock label="CV *" helper="Your CV helps your agent find evidence. It will not invent experience." error={errors.cv} testid="cv-field">
                <div className={`border-2 border-dashed rounded-2xl transition-colors ${dragActive ? 'border-[#173d35] bg-[#173d35]/5' : errors.cv ? 'border-red-500' : 'border-[#d8c8ab] bg-white/35 hover:border-[#173d35]/60'}`} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} data-testid="cv-dropzone">
                  {cvFile ? (
                    <div className="p-6 flex items-center justify-between gap-4">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#173d35]/10"><Upload className="w-5 h-5" /></div>
                        <div className="min-w-0"><p className="truncate font-medium">{cvFile.name}</p><p className="text-sm text-muted-foreground">{(cvFile.size / 1024 / 1024).toFixed(2)} MB</p></div>
                      </div>
                      <button type="button" onClick={() => { setCvFile(null); setLiveAnnouncement('CV removed'); }} className="rounded-full p-2 hover:bg-foreground/10 transition-colors" data-testid="remove-cv-btn" aria-label="Remove CV"><X className="w-5 h-5" /></button>
                    </div>
                  ) : (
                    <button id="cv-upload-control" type="button" onClick={() => cvInputRef.current?.click()} aria-describedby={errors.cv ? 'cv-field-error' : undefined} className="flex min-h-[44px] w-full flex-col items-center p-8 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#173d35] focus-visible:ring-offset-2" data-testid="cv-upload-control">
                      <Upload className="mb-3 h-8 w-8 text-muted-foreground" aria-hidden="true" />
                      <p className="mb-1"><span className="font-medium text-foreground">Click to upload</span> or drag and drop</p>
                      <p className="text-sm text-muted-foreground">PDF or DOCX, max 10MB</p>
                    </button>
                  )}
                  <input ref={cvInputRef} id="cv-file-input" type="file" accept=".pdf,.docx" onChange={handleFileChange} className="hidden" tabIndex={-1} aria-hidden="true" data-testid="input-cv" />
                </div>
                <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid="cv-privacy-copy"><Shield className="w-3.5 h-3.5 shrink-0 mt-0.5 text-muted-foreground/80" /><span>Your CV is stored privately and only used to match you with jobs and tailor your applications. <Link to="/privacy-policy" target="_blank" className="underline hover:no-underline">Read our privacy policy</Link>.</span></p>
              </FieldBlock>
              {isExactJobFlow && (
                <div className="space-y-3 rounded-2xl border border-[#d8c8ab] bg-white/45 p-4" data-testid="exact-job-consent-card">
                  <div className="flex items-start gap-3">
                    <Checkbox id="consent" checked={consentGiven} aria-describedby={errors.consent ? 'consent-error' : undefined} onCheckedChange={(checked) => { setConsentGiven(checked); if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' })); }} className="mt-1" data-testid="consent-checkbox" />
                    <Label htmlFor="consent" className="cursor-pointer text-sm leading-relaxed">I agree that JobMatchPro can process my CV to provide the matching service I requested.</Label>
                  </div>
                  {errors.consent && <p id="consent-error" className="text-sm text-red-500" role="alert">{safeFindJobsText(errors.consent)}</p>}
                </div>
              )}
            </StepShell>
            </>
          )}

          {/* Step 3: Current context */}
          {effectiveCurrentStep === 3 && (
            <StepShell step={3} title="Add current context" purpose="This gives your direction a little background without turning it into a long profile form." testid="step-3-form">
              <FieldBlock label="Current job title" htmlFor="job_title" helper="Optional. Add your current or most recent role if it helps explain your background." testid="job-title-field">
                <Input id="job_title" name="job_title" placeholder="e.g., Software Engineer" value={formData.job_title} onChange={handleInputChange} className="form-input text-base py-4" data-testid="input-job-title" />
                {maybeJunkShortValue(formData.job_title) && <p className="text-sm text-amber-700" data-testid="job-title-soft-warning">This looks very short. You can keep it, or make it clearer for better matching.</p>}
              </FieldBlock>
              <FieldBlock label="Current location" htmlFor="location" helper="Optional. This can help with commute and market context." testid="location-field">
                <Input id="location" name="location" placeholder="e.g., Munich, Germany" value={formData.location} onChange={handleInputChange} className="form-input text-base py-4" data-testid="input-location" />
                {maybeJunkShortValue(formData.location) && <p className="text-sm text-amber-700" data-testid="location-soft-warning">This looks very short. You can keep it, or make it clearer for better matching.</p>}
              </FieldBlock>
              <FieldBlock label="Years of experience" helper="This helps avoid roles that are clearly too senior or too junior." testid="experience-field">
                <SingleOptionGrid options={EXPERIENCE_BAND_OPTIONS} selected={formData.years_experience_band} onSelect={(value) => handleSelectChange('years_experience_band', value)} testidPrefix="experience-band" />
              </FieldBlock>
              <FieldBlock label="Why are you considering a move now?" helper="Optional. It helps your agent frame applications. You can keep it private." testid="reason-for-looking-field">
                <SingleOptionGrid options={reasonForLookingOptions} selected={formData.reason_for_looking} onSelect={(value) => handleSelectChange('reason_for_looking', value)} testidPrefix="reason-for-looking" />
              </FieldBlock>
              <FieldBlock label="When could you start a new role?" helper="Roles often state start dates. This keeps mismatched ones out of your way." testid="availability-field">
                <SingleOptionGrid options={availabilityOptions} selected={formData.availability} onSelect={(value) => handleSelectChange('availability', value)} testidPrefix="availability" />
              </FieldBlock>
              {(formData.job_title || formData.reason_for_looking === 'My current role or contract is ending') && (
                <FieldBlock label="Notice period" helper="Optional. Add this only if it is relevant to your current situation." testid="notice-period-field">
                  <SingleOptionGrid options={noticePeriodOptions} selected={formData.notice_period} onSelect={(value) => handleSelectChange('notice_period', value)} testidPrefix="notice-period" />
                </FieldBlock>
              )}
            </StepShell>
          )}

          {effectiveCurrentStep === 4 && (
            <StepShell step={4} title="Add skills and keywords" purpose="These are search signals. We will keep them separate from confirmed CV evidence until you choose otherwise." testid="step-4-form">
              <FieldBlock label="Skills and keywords" htmlFor="core_skills" helper="Add skills, tools, or keywords you want the search to recognise. We will also check your CV for evidence." testid="skills-field">
                <Textarea id="core_skills" name="core_skills" placeholder="e.g., Python, Data Analysis, Project Management" value={formData.core_skills} onChange={handleInputChange} className="form-input min-h-[96px]" data-testid="input-skills" />
                <DirectionChipGroup label="Search skills" values={formData.core_skills} emptyText="Add skills" testid="search-skills-chip-preview" />
              </FieldBlock>
            </StepShell>
          )}

          {effectiveCurrentStep === 5 && (
            <StepShell step={5} title="How do you work best?" purpose="Pick up to three. No right answers, and nothing here filters jobs out." testid="step-5-form">
              <LocalOptionGrid options={waysOfWorkingOptions} selected={formData.waysOfWorking} max={3} onToggle={(option) => toggleLocalArrayField('waysOfWorking', option, 3)} testidPrefix="ways-of-working" />
              <p className="text-sm text-[#6f6657]" aria-live="polite" data-testid="ways-of-working-selection-count">
                Selected {uniqueArray(formData.waysOfWorking || []).length} of 3.
              </p>
              <DirectionChipGroup label="Selected working preferences" values={formData.waysOfWorking || []} emptyText="Skipped for now" testid="ways-of-working-chip-preview" />
            </StepShell>
          )}

          {effectiveCurrentStep === 6 && (
            <StepShell step={6} title={hasGermanRelevantMarket(formData) ? 'Add language details' : 'Which languages can you work in?'} purpose={hasGermanRelevantMarket(formData) ? 'Language details help when roles mention specific language requirements.' : 'Add languages you can use professionally. You can update these later.'} testid="step-6-form">
              {hasGermanRelevantMarket(formData) && <FieldBlock label="German level" helper="Useful for roles that mention German requirements. Leave blank if not relevant." testid="german-field">
                <SingleOptionGrid options={GERMAN_LEVEL_OPTIONS} selected={formData.german_level} onSelect={(value) => handleSelectChange('german_level', value)} testidPrefix="german-level" />
              </FieldBlock>}
              <FieldBlock label="Other languages" htmlFor="languages" helper="Add languages you can use professionally." testid="languages-field">
                <div className="space-y-3" data-testid="language-row-list">
                  {(normalizeLanguageRows(formData.languageRows).length ? normalizeLanguageRows(formData.languageRows) : [{ language: '', proficiency: '' }]).map((row, index) => (
                    <div key={`language-row-${index}`} className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto]" data-testid={`language-row-${index}`}>
                      <Input
                        id={index === 0 ? 'languages' : undefined}
                        placeholder="Language, for example English"
                        value={row.language}
                        onChange={(event) => updateLanguageRow(index, 'language', event.target.value)}
                        className="form-input text-base py-4"
                        data-testid={`input-language-${index}`}
                        aria-label={`Language ${index + 1}`}
                      />
                      <div className="sm:col-span-2" data-testid={`language-proficiency-${index}`}>
                        <SingleOptionGrid options={LANGUAGE_PROFICIENCY_OPTIONS} selected={row.proficiency} onSelect={(value) => updateLanguageRow(index, 'proficiency', value)} testidPrefix={`language-${index}-proficiency`} />
                      </div>
                      <button type="button" onClick={() => removeLanguageRow(index)} className="min-h-[44px] rounded-md border border-[#8b7652] px-3 py-2 text-sm text-[#173d35]" aria-label={`Remove ${row.language || `language ${index + 1}`}`} data-testid={`remove-language-${index}`}>Remove</button>
                    </div>
                  ))}
                  <button type="button" onClick={addLanguageRow} className="min-h-[44px] rounded-md border border-[#8b7652] bg-white/45 px-4 py-2 text-sm font-medium text-[#173d35]" data-testid="add-language-row-btn">+ Add another language</button>
                </div>
                <DirectionChipGroup label="Professional languages" values={formatLanguageRows(formData.languageRows)} emptyText="Add English or another language" testid="language-chip-preview" />
              </FieldBlock>
            </StepShell>
          )}

          {effectiveCurrentStep === 7 && (
            <StepShell step={7} title="Add work authorisation context" purpose="This is optional context, not a judgement or a rejection gate." testid="step-7-form">
              <FieldBlock label="Work authorisation context" helper="This helps your agent understand which roles may need closer review. It is optional and is not a rejection gate." testid="visa-field">
                <SingleOptionGrid options={WORK_AUTHORISATION_OPTIONS} selected={formData.visa_status} onSelect={(value) => handleSelectChange('visa_status', value)} testidPrefix="work-authorisation" />
              </FieldBlock>
              <div className="rounded-2xl border border-[#d8c8ab] bg-white/45 p-4 text-sm leading-relaxed text-[#6f6657]" data-testid="visa-context-note">Used to improve your matching. It is not visible to recruiters. We will not infer this from your CV.</div>
            </StepShell>
          )}

          {effectiveCurrentStep === 8 && (
            <StepShell step={8} title="Is there anything you would rather keep off your list?" purpose="Optional. These answers stay private in this frontend phase. Firm-no marks are saved as guidance for now and do not yet exclude jobs." testid="step-8-form">
              <FieldBlock label="Things to avoid" helper="Choose up to 4. These answers always stay private and never appear in recruiter summaries, application documents, search ranking or scoring." testid="things-to-avoid-field">
                <LocalOptionGrid options={thingsToAvoidOptions} selected={formData.thingsToAvoid} max={4} onToggle={(option) => toggleLocalArrayField('thingsToAvoid', option, 4)} testidPrefix="things-to-avoid" />
                <p className="text-sm text-[#6f6657]" aria-live="polite" data-testid="things-to-avoid-selection-count">
                  Selected {uniqueArray(formData.thingsToAvoid || []).length} of 4.
                </p>
              </FieldBlock>
              {uniqueArray(formData.thingsToAvoid || []).length > 0 && (
                <div className="rounded-2xl border border-[#d8c8ab] bg-white/45 p-4" data-testid="firm-no-controls">
                  <p className="mb-2 text-sm font-medium text-[#22362f]">Mark firm no items</p>
                  <p className="mb-3 text-sm leading-relaxed text-[#6f6657]">Firm-no marks are stored as private guidance for now. They do not exclude jobs until a later approved matching rule exists.</p>
                  <SelectablePillRow options={uniqueArray(formData.thingsToAvoid || [])} selected={formData.firmNoItems || []} onToggle={toggleFirmNoItem} testidPrefix="firm-no" />
                </div>
              )}
            </StepShell>
          )}

          {effectiveCurrentStep === 9 && (
            <StepShell step={9} title="What salary floor should your agent consider?" purpose="Optional. Salary floors are saved in this browser draft for now. They do not yet change backend matching." testid="step-9-form">
              <div className="space-y-4" data-testid="market-salary-sections">
                {activeSalaryMarkets(formData).length ? activeSalaryMarkets(formData).map((market) => {
                  const floor = salaryFloorForMarket(market, formData.salaryFloorsByMarket?.[market]);
                  const key = market.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
                  const label = SALARY_MARKET_CONFIG[market]?.label || market;
                  return (
                    <div key={market} className="rounded-2xl border border-[#d8c8ab] bg-white/45 p-4" data-testid={`salary-section-${key}`}>
                      <FieldBlock label={`Minimum salary for ${label}`} htmlFor={`salary_${key}_amount`} helper={market === 'Remote Europe' ? 'Optional. Choose a currency only if you enter a floor for Remote Europe.' : 'Optional. Leave blank if salary is not important yet.'} error={errors[`salary_${key}_amount`]} testid={`salary-${key}-field`}>
                        <Input id={`salary_${key}_amount`} type="number" inputMode="decimal" min="0" placeholder="e.g., 65000" value={floor.amount} onChange={(e) => handleSalaryFloorChange(market, 'amount', e.target.value)} className={`form-input text-base py-4 ${errors[`salary_${key}_amount`] ? 'border-red-500' : ''}`} data-testid={`input-salary-${key}-amount`} />
                      </FieldBlock>
                      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                        {market === 'Remote Europe' ? (
                          <FieldBlock label="Currency" testid={`salary-${key}-currency-field`} error={errors[`salary_${key}_currency`]}>
                            <SingleOptionGrid options={['EUR', 'GBP']} selected={floor.currency} onSelect={(value) => handleSalaryFloorChange(market, 'currency', value)} testidPrefix={`salary-${key}-currency`} />
                          </FieldBlock>
                        ) : (
                          <div className="rounded-xl border border-[#d8c8ab] bg-white/55 p-3 text-sm text-[#30473e]" data-testid={`salary-${key}-fixed-currency`}>
                            <p className="text-xs uppercase tracking-[0.14em] text-[#7b694d]">Currency</p>
                            <p className="mt-1 font-medium">{floor.currency}</p>
                          </div>
                        )}
                        <FieldBlock label="Period" testid={`salary-${key}-period-field`} error={errors[`salary_${key}_period`]}>
                          <SingleOptionGrid options={SALARY_PERIOD_OPTIONS} selected={floor.period} onSelect={(value) => handleSalaryFloorChange(market, 'period', value)} testidPrefix={`salary-${key}-period`} />
                        </FieldBlock>
                      </div>
                    </div>
                  );
                }) : (
                  <div className="rounded-2xl border border-[#d8c8ab] bg-white/45 p-4 text-sm leading-relaxed text-[#6f6657]" data-testid="salary-skipped-market-note">
                    Salary can stay skipped until your target market is clearer. We will not ask you to choose the same market again here.
                  </div>
                )}
              </div>
              <FieldBlock label="Education status" helper="Optional. Add details only if they may not be clear from your CV." testid="education-status-field">
                <SingleOptionGrid options={EDUCATION_STATUS_OPTIONS} selected={formData.education_status} onSelect={(value) => handleSelectChange('education_status', value)} testidPrefix="education-status" />
              </FieldBlock>
              {(formData.education_status === 'I want to add it manually' || formData.education) && (
                <FieldBlock label="Education details" htmlFor="education" helper="Optional. Add your highest relevant education only if it may not be clear from your CV." testid="education-field">
                  <Textarea id="education" name="education" placeholder="e.g., MSc Computer Science, TU Munich, 2022" value={formData.education} onChange={handleInputChange} className="form-input min-h-[80px]" data-testid="input-education" />
                </FieldBlock>
              )}
            </StepShell>
          )}

          {effectiveCurrentStep === 10 && (
            <StepShell step={10} title="Before we create your search" purpose="We use your CV and answers to build your Master CV, match you with roles and prepare documents you request." testid="step-10-form">
              <div className="rounded-2xl border border-[#d8c8ab] bg-white/45 p-4 text-sm leading-relaxed text-[#6f6657]" data-testid="consent-transparency-card">
                <ul className="list-disc space-y-2 pl-5">
                  <li>Your CV file and extracted text are used for matching and document preparation.</li>
                  <li>You review and correct your Master CV before the search starts.</li>
                  <li>Recruiters cannot currently view your profile or CV.</li>
                  <li>We do not sell your candidate data.</li>
                  <li>We do not use your CV to train unrelated models.</li>
                  <li>You can request deletion of your account, CV and generated documents.</li>
                  <li>Motivations and ways-of-working answers remain private unless you separately approve recruiter-facing use. Things you choose to avoid always remain private and are never included in recruiter summaries or application documents.</li>
                </ul>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="final-intake-review">
                <DirectionChipGroup label="Roles" values={formData.target_roles} emptyText="Add roles" testid="final-review-roles" />
                <DirectionChipGroup label="Markets" values={formData.target_locations} emptyText="Add markets" testid="final-review-markets" />
                <DirectionChipGroup label="Level" values={formData.target_seniority_levels?.length ? formData.target_seniority_levels : formData.target_seniority} emptyText="Optional" testid="final-review-level" />
                <DirectionChipGroup label="Skills" values={formData.core_skills} emptyText="Optional" testid="final-review-skills" />
              </div>
              <div className="space-y-3 rounded-2xl border border-[#d8c8ab] bg-white/45 p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="consent"
                    checked={consentGiven}
                    aria-describedby={errors.consent ? 'consent-error' : undefined}
                    onCheckedChange={(checked) => {
                      setConsentGiven(checked);
                      if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
                    }}
                    className="mt-1"
                    data-testid="consent-checkbox"
                  />
                  <Label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer">
                    I agree that JobMatchPro can process my CV and answers to provide the matching and application-document services I request.
                  </Label>
                </div>
                <Link to="/privacy-policy" target="_blank" className="inline-flex text-xs text-[#6f6657] underline hover:no-underline" data-testid="consent-privacy-link">Privacy Policy</Link>
                {errors.consent && <p id="consent-error" className="text-sm text-red-500">{safeFindJobsText(errors.consent)}</p>}
              </div>
            </StepShell>
          )}

          {/* Error Message */}
          {errors.submit && <p className="text-sm text-red-500 text-center" data-testid="submit-error">{safeFindJobsText(errors.submit)}</p>}

          {/* Navigation Buttons */}
          {effectiveCurrentStep >= 1 && (
            <div className="flex flex-col-reverse gap-3 border-t border-[#d8c8ab] pt-6 sm:flex-row sm:items-center sm:justify-between">
              {effectiveCurrentStep > 1 && !(isExactJobFlow && effectiveCurrentStep === 2) ? (
                <Button type="button" onClick={handleBack} className="btn-secondary" data-testid="back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              ) : (
                <div />
              )}
              {effectiveCurrentStep < TOTAL_FIND_JOBS_STEPS ? (
                <Button type="button" onClick={handleNext} className="btn-primary" disabled={isExactJobFlow && effectiveCurrentStep === 2 && isSubmitting} data-testid="next-btn">
                  {isExactJobFlow && effectiveCurrentStep === 2 ? (
                    isSubmitting ? 'Reviewing your CV...' : <>Review my CV evidence <Send className="w-4 h-4 ml-2" /></>
                  ) : (
                    <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              ) : (
                <Button type="submit" className="btn-primary" disabled={isSubmitting} data-testid="submit-btn">
                  {isSubmitting ? 'Submitting...' : (
                    <>Create search draft <Send className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              )}
            </div>
          )}
        </form>
        <CareerAgentFindJobsPanel
          formData={formData}
          cvFile={cvFile}
          candidateIntakeDraft={candidateIntakeDraft}
          currentStep={effectiveCurrentStep}
        />
        </div>
      </div>
    </div>
  );
}
